/**
 * 🖨️ Etikettendrucker-Service (Bluetooth, Android-only)
 *
 * Spricht einen gekoppelten Bixolon SPP-R200III (oder anderen ESC/POS-Drucker)
 * über das native BtPrint-Plugin an. Druckt nach dem Speichern eines Pakets ein
 * selbstklebendes Etikett: Hausnummer groß, darunter klein Name + Einlagerungsdatum.
 *
 * Komplett optional: Ist kein Drucker konfiguriert, passiert nichts (kein UI,
 * kein Fehler) — Nutzer ohne Drucker merken von dem Feature nichts.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { hasCustomFeature } from './customFeatures'; // 🧩 Nachname-Sortierung nur für freigeschaltete Konten
import { platzhalterCode, formatCode, paketKurzCode, isVintedSortOn, vintedCodeAusTracking } from './vintedNames'; // 📦 Vinted-Kurznummer fürs Etikett
import { reportError } from './errorReport'; // 🧯 Druckfehler fürs Admin-Board

// 🌍 Fehlermeldungen in der App-Sprache. Bewusst per dynamischem Import + deutschem Rückfall:
// die Etikett-Tests laden diese Datei als Kopie in Node, dort gibt es die Sprachdateien nicht.
async function uiText(key, deutsch, vars) {
  try {
    const { tGlobal } = await import('../utils/tGlobal');
    const s = tGlobal(key, vars);
    return s && s !== key ? s : deutsch;
  } catch { return deutsch; }
}

const BtPrint = registerPlugin('BtPrint');

const PRINTER_KEY = 'kiosk_label_printer';      // JSON {address, name}
const AUTOPRINT_KEY = 'kiosk_label_autoprint';  // '1' | '0'
// Druckersprache — EIGENER Key, damit die bestehende Drucker-Config unberührt bleibt.
// Fehlt er (alle bisherigen Geräte) → 'cpcl' = unveränderter Bixolon-Druck.
const LANG_KEY = 'kiosk_label_lang';            // 'cpcl' | 'zpl'
// Etikett-Layout (nur ZPL): 'hausnummer' (Standard) | 'nachname' (Sortierung nach Nachname)
const LAYOUT_KEY = 'kiosk_label_layout';

// ============================================================
// Konfiguration (localStorage)
// ============================================================

// 🧪 Schein-Drucker für Tests im Browser — NUR in der Setup-Vorschau und nur mit eigenem Schalter.
// Nimmt Druckaufträge an und legt sie in window.__simPrints ab; so lässt sich der ganze Weg
// (Koppeln → Testdruck → Etikett) ohne Gerät prüfen. Auf normalen Geräten nie aktiv.
function simAktiv() {
  try { return localStorage.getItem('kiosk_setup_preview') === '1' && localStorage.getItem('kiosk_printer_sim') === '1'; } catch { return false; }
}
function simLesen(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return v === null ? fallback : v; } catch { return fallback; }
}
async function simDruck(job) {
  const fehler = simLesen('kiosk_printer_sim_error', null);
  if (fehler) throw new Error(String(fehler));
  try { (window.__simPrints = window.__simPrints || []).push({ address: job.address, data: job.data, at: Date.now() }); } catch { /* */ }
  return { bondMs: 0, connectMs: 5, sendMs: 5, attempts: 1 };
}
const SimBt = {
  isEnabled: async () => ({ enabled: simLesen('kiosk_printer_sim_bt', true) !== false }),
  requestPermissions: async () => ({ btconnect: simLesen('kiosk_printer_sim_perm', true) === false ? 'denied' : 'granted' }),
  listPaired: async () => {
    if (simLesen('kiosk_printer_sim_perm', true) === false) throw new Error('Bluetooth-Berechtigung fehlt');
    return { devices: simLesen('kiosk_printer_sim_devices', [
      { name: 'JBL Flip 6', address: '11:22:33:44:55:66' },
      { name: 'PC45d-8C3B4A', address: '8C:3B:4A:9B:68:F1' },
      { name: 'SPP-R200III', address: '74:F0:7D:E6:11:22' },
    ]) };
  },
  btStatus: async () => ({ devices: [] }),
  print: (job) => simDruck(job),
  printTcp: (job) => simDruck({ address: `tcp://${job.host}:${job.port}`, data: job.data }),
};
const bt = () => (simAktiv() ? SimBt : BtPrint);

export function isPrinterSupported() {
  if (simAktiv()) return true;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('kiosk_remote_print_client') === '1') return true;
  } catch { /* */ }
  // Classic-Bluetooth-SPP nur nativ unter Android
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Bench-Auslöser: native Volume-Tasten als Foto-Auslöser einfangen (statt
 * Lautstärke). Aktiv nur, solange die Bench offen ist.
 */
export async function setHardwareTrigger(enabled) {
  if (!isPrinterSupported()) return;
  try { await BtPrint.setVolumeTrigger({ enabled: !!enabled }); } catch { /* alt: Plugin ohne Methode */ }
}

/**
 * Vollbild/Immersive nativ (Status-/Navigationsleiste ausblenden). Der Web-
 * Fullscreen-API funktioniert in der Android-WebView nicht zuverlässig.
 */
export async function setImmersive(enabled) {
  if (!isPrinterSupported()) return;
  try { await BtPrint.setImmersive({ enabled: !!enabled }); } catch { /* alt: Plugin ohne Methode */ }
}

/**
 * Geräte-Temperatur (Kamera-Station): {batteryTempC, thermalStatus} oder null —
 * z. B. auf alten APKs ohne die native Methode oder im Browser.
 */
export async function getThermal() {
  if (!isPrinterSupported()) return null;
  try {
    const r = await BtPrint.getThermal();
    return (r && (typeof r.batteryTempC === 'number' || typeof r.thermalStatus === 'number')) ? r : null;
  } catch { return null; }
}

export function getPrinterConfig() {
  try {
    const raw = localStorage.getItem(PRINTER_KEY);
    if (raw) return JSON.parse(raw);
    if (localStorage.getItem('kiosk_remote_print_client') === '1') {
      return { name: '🌐 Shop-Druck-Brücke', address: 'remote-bridge', remote: true };
    }
    return null;
  } catch {
    return null;
  }
}

export function setPrinterConfig(config) {
  if (config) localStorage.setItem(PRINTER_KEY, JSON.stringify(config));
  else localStorage.removeItem(PRINTER_KEY);
  try {
    if (typeof window !== 'undefined') {
      import('./deviceService').then((m) => m.sendDeviceHeartbeat?.()).catch(() => {});
    }
  } catch { /* ignore */ }
}

export function getPrinterLang() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === 'zpl' || v === 'cpcl') return v;
    if (hasCustomFeature('grenzpaket') && guessPrinterLang(getPrinterConfig()?.name) !== 'cpcl') return 'zpl';
    return 'cpcl';
  } catch { return 'cpcl'; }
}

export function setPrinterLang(lang) {
  try { localStorage.setItem(LANG_KEY, lang === 'zpl' ? 'zpl' : 'cpcl'); } catch { /* */ }
}

/**
 * Tipp anhand des BT-Namens: Honeywell-/Zebra-Desktopdrucker sprechen kein CPCL,
 * wohl aber ZPL (Honeywell: ZSim). null = kein eindeutiger Hinweis → nichts ändern.
 */
export function guessPrinterLang(deviceName) {
  const n = String(deviceName || '');
  if (/\b(PC4[2-5]|PD4[35]|PM4[2-5]|PX[46]|Honeywell|Intermec|Zebra|Z[DTQ]\d|G[KX]4\d|TSC|TE2\d0|TTP-|DA2\d0)/i.test(n)) return 'zpl';
  if (/bixolon|SPP-|SRP-|XM7/i.test(n)) return 'cpcl';
  return null;
}

// 🔧 Selbstreparatur beim App-Start (25.10.2026): Ist ein eindeutig als ZPL erkannter Tischdrucker
// (TSC/Honeywell/Zebra) gekoppelt, stelle sicher, dass die Druckersprache beim Start auf 'zpl' steht
// (verhindert, dass ein Gerät nach zweimal „Nein" beim Testdruck oder nach dem Setup auf 'cpcl'
// hängenbleibt — Bluetooth sendet CPCL sonst ohne Fehlermeldung, aber der ZPL-Drucker schweigt).
try {
  if (typeof localStorage !== 'undefined' && guessPrinterLang(getPrinterConfig()?.name) === 'zpl') {
    localStorage.setItem(LANG_KEY, 'zpl');
  }
} catch { /* */ }

// 📏 Etikettengröße (nur ZPL): '100x150' (Standard) | '100x75' (= 4×3 Zoll / ZEGA 102×76 mm) | 'endlos' (Endlos-Rolle, abreißen)
const SIZE_KEY = 'kiosk_label_size';
export const LABEL_SIZES = {
  '100x150': { h: 1218 },
  '100x75': { h: 609 },   // 812 × 609 Punkte @ 203 dpi = 101,6 × 76,2 mm (passend für ZEGA 102×76 mm, Art.-Nr. B101020760001)
  '102x76': { h: 609 },   // Alias für ZEGA 102×76×25 (930 Etiketten/Rolle)
  endlos: { h: null },    // Höhe = Inhalt; Drucker läuft ohne Lückensuche (^MNN)
};
// Eigene Größe: 'BREITExHÖHE' in Millimetern, z. B. '80x50'; Höhe 0 = Endlos-Rolle ('60x0').
// 203 dpi → 8,12 Punkte pro Millimeter (100 mm = 812 Punkte, wie die festen Größen).
export const LABEL_MM = { wMin: 30, wMax: 110, hMin: 20, hMax: 300 };
export function parseLabelSize(key) {
  if (LABEL_SIZES[key]) return { w: 812, h: LABEL_SIZES[key].h };
  const m = /^(\d{2,3})x(\d{1,3})$/.exec(String(key || ''));
  if (!m) return null;
  const wmm = +m[1], hmm = +m[2];
  if (wmm < LABEL_MM.wMin || wmm > LABEL_MM.wMax) return null;
  if (hmm !== 0 && (hmm < LABEL_MM.hMin || hmm > LABEL_MM.hMax)) return null;
  return { w: Math.round(wmm * 8.12), h: hmm === 0 ? null : Math.round(hmm * 8.12) };
}
export function getLabelSize() {
  try {
    const k = localStorage.getItem(SIZE_KEY);
    if (parseLabelSize(k)) return k === '102x76' ? '100x75' : k;
    if (hasCustomFeature('grenzpaket')) return '100x75'; // 🇨🇭 Grenzpaket nutzt ZEGA 102×76 mm (4×3 Zoll = 812×609 Punkte)
    return '100x150';
  } catch { return '100x150'; }
}
export function setLabelSize(k) {
  try { localStorage.setItem(SIZE_KEY, parseLabelSize(k) ? k : '100x150'); } catch { /* */ }
}

// ⬛ Schwarzer Rahmen ums Etikett (nur ZPL) — Sichtbarkeit auf weißen Etiketten statt teurer gelber
const FRAME_KEY = 'kiosk_label_rahmen';
export function isLabelFrameOn() { try { return localStorage.getItem(FRAME_KEY) === '1'; } catch { return false; } }
export function setLabelFrameOn(on) { try { localStorage.setItem(FRAME_KEY, on ? '1' : '0'); } catch { /* */ } }

export function getLabelLayout() {
  let k = null;
  try { k = localStorage.getItem(LAYOUT_KEY); } catch { /* */ }
  if (k === 'eigen') return 'eigen'; // 🎨 selbst zusammengestelltes Etikett
  if (hasCustomFeature('grenzpaket') && (k === 'grenzpaket' || !k)) return 'grenzpaket'; // 🇨🇭 Original Grenzpaket-Quittung (Standard für Grenzpaket-Konten)
  if (!hasCustomFeature('nachnameSortierung')) return 'hausnummer'; // Sonderfunktion (Konto)
  return k === 'nachname' ? 'nachname' : 'hausnummer';
}

export function setLabelLayout(layout) {
  const k = layout === 'nachname' || layout === 'eigen' || layout === 'grenzpaket' ? layout : 'hausnummer';
  try { localStorage.setItem(LAYOUT_KEY, k); } catch { /* */ }
}

// 🎨 Eigenes Layout (nur ZPL): was groß oben steht und welche Zeilen darunter gedruckt werden.
const HEAD_KEY = 'kiosk_label_kopf';     // 'hausnummer' | 'nachname' | 'name' | 'initialen' | 'fach'
const LINES_KEY = 'kiosk_label_zeilen';  // Kommaliste aus LABEL_ZEILEN in DRUCK-Reihenfolge; 'keine' = nur die Kopfzeile
export const LABEL_KOEPFE = ['hausnummer', 'nachname', 'name', 'initialen', 'fach'];
export const LABEL_ZEILEN = ['fach', 'name', 'initialen', 'strasse', 'absender', 'dienst', 'datum'];
// Paketdienst und Datum stehen immer zusammen in der Fußzeile; die übrigen Zeilen in der gewählten Reihenfolge
export const LABEL_FUSS = ['dienst', 'datum'];
const ZEILEN_STANDARD = ['fach', 'name', 'datum'];
export function getLabelKopf() {
  try { const k = localStorage.getItem(HEAD_KEY); return LABEL_KOEPFE.includes(k) ? k : 'hausnummer'; } catch { return 'hausnummer'; }
}
export function setLabelKopf(k) {
  try { localStorage.setItem(HEAD_KEY, LABEL_KOEPFE.includes(k) ? k : 'hausnummer'); } catch { /* */ }
}
export function parseLabelZeilen(text) {
  if (text === 'keine') return [];
  if (typeof text !== 'string' || !text) return [...ZEILEN_STANDARD];
  // Reihenfolge so, wie gespeichert (gültige Einträge, jeder nur einmal)
  return text.split(',').filter((z, i, a) => LABEL_ZEILEN.includes(z) && a.indexOf(z) === i);
}
export function formatLabelZeilen(liste) {
  const z = (liste || []).filter((x, i, a) => LABEL_ZEILEN.includes(x) && a.indexOf(x) === i);
  return z.length ? z.join(',') : 'keine';
}
export function getLabelZeilen() {
  try { return parseLabelZeilen(localStorage.getItem(LINES_KEY)); } catch { return [...ZEILEN_STANDARD]; }
}
export function setLabelZeilen(liste) {
  try { localStorage.setItem(LINES_KEY, formatLabelZeilen(liste)); } catch { /* */ }
}

// 🚚 Paketdienst (DHL, Hermes …) mit aufs Standard-Etikett drucken — Standard: aus
const CARRIER_KEY = 'kiosk_label_dienst';
export function isLabelCarrierOn() { try { return localStorage.getItem(CARRIER_KEY) === '1'; } catch { return false; } }
export function setLabelCarrierOn(on) { try { localStorage.setItem(CARRIER_KEY, on ? '1' : '0'); } catch { /* */ } }

// 📐 Ränder (nur ZPL, gilt NUR für dieses Gerät — sie gleichen aus, was der einzelne Drucker oben,
// unten oder seitlich nicht bedruckt bzw. wie das fertige Etikett geschnitten ist).
// 'oben,unten,seite' in Millimetern. Oben darf negativ sein (Inhalt weiter nach oben ziehen).
// Fehlt der Wert oder ist er '0,0,0', bleibt die Ausgabe byteidentisch zu vorher.
// ── Code aufs Etikett: Barcode (Code 128) und/oder QR-Code der Sendungsnummer (Wunsch Tim, 10.10.2026) ──────
// Der Drucker erzeugt den Code aus den DATEN neu (kein Bild vom Original) — Inhalt wie der gescannte Code, damit
// Vinted-App bzw. DHL-Portal unser Etikett genauso lesen wie den Original-Aufkleber.
const CODE_KEY = 'kiosk_label_code'; // 'keine' | '<barcode|qr|beide>:<klein|mittel|gross>'
export const LABEL_CODE_ARTEN = ['keine', 'barcode', 'qr', 'beide'];
export const LABEL_CODE_GROESSEN = ['klein', 'mittel', 'gross'];
export function parseLabelCode(text) {
  const m = /^(barcode|qr|beide):(klein|mittel|gross)$/.exec(String(text || ''));
  return m ? { art: m[1], groesse: m[2] } : { art: 'keine', groesse: 'mittel' };
}
export function formatLabelCode(c) {
  if (!c || !LABEL_CODE_ARTEN.includes(c.art) || c.art === 'keine') return 'keine';
  return `${c.art}:${LABEL_CODE_GROESSEN.includes(c.groesse) ? c.groesse : 'mittel'}`;
}
export function getLabelCode() { try { return parseLabelCode(localStorage.getItem(CODE_KEY)); } catch { return parseLabelCode(''); } }
export function setLabelCode(c) { try { localStorage.setItem(CODE_KEY, formatLabelCode(c)); } catch { /* */ } }
/** Inhalt des Codes: die Sendungsnummer wie gescannt (A–Z, 0–9, Bindestrich), '' = nichts drucken */
export function codeDaten(paket) {
  const t = String(paket?.trackingNumber || '').trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
  return t.length >= 6 ? t.slice(0, 40) : '';
}
// Breite eines Code-128-Strichcodes in Modulen: ~11 je Zeichen + 35 (Start/Prüf/Stopp); reine Ziffern paarweise
const code128Module = (daten) => ((/^\d+$/.test(daten) ? Math.ceil(daten.length / 2) : daten.length) * 11 + 35);
const QR_MODULE = 29; // Platzrechnung: Version 3 reicht für bis zu 35 Zeichen

const RAND_KEY = 'kiosk_label_rand';
export const LABEL_RAND = { obenMin: -8, obenMax: 30, untenMax: 30, seiteMax: 15 };
const RAND_NULL = { oben: 0, unten: 0, seite: 0 };
export function parseLabelRand(text) {
  const m = /^(-?\d{1,2}),(\d{1,2}),(\d{1,2})$/.exec(String(text || ''));
  if (!m) return { ...RAND_NULL };
  const grenze = (v, min, max) => Math.max(min, Math.min(max, v));
  return {
    oben: grenze(+m[1], LABEL_RAND.obenMin, LABEL_RAND.obenMax),
    unten: grenze(+m[2], 0, LABEL_RAND.untenMax),
    seite: grenze(+m[3], 0, LABEL_RAND.seiteMax),
  };
}
export function formatLabelRand(r) {
  const p = parseLabelRand(`${Math.round((r && r.oben) || 0)},${Math.round((r && r.unten) || 0)},${Math.round((r && r.seite) || 0)}`);
  return `${p.oben},${p.unten},${p.seite}`;
}
export function getLabelRand() {
  try { return parseLabelRand(localStorage.getItem(RAND_KEY)); } catch { return { ...RAND_NULL }; }
}
export function setLabelRand(r) {
  try { localStorage.setItem(RAND_KEY, formatLabelRand(r)); } catch { /* */ }
}

// 💬 Eigener Grußtext unter dem Kasten (Wunsch Folkert 15.10.2026: „Have a nice day! The Green GOAT")
const GRUSS_KEY = 'kiosk_label_gruss';
export const LABEL_GRUSS_MAX = 60;
export function parseLabelGruss(text) {
  return String(text || '').replace(/[\r\n\t]+/g, ' ').replace(/[^\x20-\x7e\u00a0-\u024f]/g, '').slice(0, LABEL_GRUSS_MAX);
}
export function getLabelGruss() {
  try { return parseLabelGruss(localStorage.getItem(GRUSS_KEY)); } catch { return ''; }
}
export function setLabelGruss(text) {
  try { localStorage.setItem(GRUSS_KEY, parseLabelGruss(text)); } catch { /* */ }
}

// 💝 Untere Etikett-Hälfte: Eigener QR-Code-Link + mehrzeiliger Infotext (Wunsch Folkert 04.11.2026: Crowdfunding / Spende / Website)
const QR_URL_KEY = 'kiosk_label_qr_url';
const QR_TEXT_KEY = 'kiosk_label_qr_text';
export const LABEL_QR_URL_MAX = 180;
export const LABEL_QR_TEXT_MAX = 280;
export function parseLabelQrUrl(text) {
  return String(text || '').trim().replace(/[\s\r\n\t]+/g, '').replace(/[^\x21-\x7e]/g, '').slice(0, LABEL_QR_URL_MAX);
}
export function getLabelQrUrl() {
  try { return parseLabelQrUrl(localStorage.getItem(QR_URL_KEY)); } catch { return ''; }
}
export function setLabelQrUrl(url) {
  try {
    const v = parseLabelQrUrl(url);
    if (v) localStorage.setItem(QR_URL_KEY, v);
    else localStorage.removeItem(QR_URL_KEY);
  } catch { /* */ }
}
export function parseLabelQrText(text) {
  return String(text || '').replace(/\r\n?/g, '\n').replace(/\t/g, ' ').replace(/[^\x20-\x7e\u00a0-\u024f\n]/g, '').slice(0, LABEL_QR_TEXT_MAX);
}
export function getLabelQrText() {
  try { return parseLabelQrText(localStorage.getItem(QR_TEXT_KEY)); } catch { return ''; }
}
export function setLabelQrText(text) {
  try {
    const v = parseLabelQrText(text);
    if (v.trim()) localStorage.setItem(QR_TEXT_KEY, v);
    else localStorage.removeItem(QR_TEXT_KEY);
  } catch { /* */ }
}
// QR-Code-Modulanzahl für Fehlerkorrektur Q (^FDQA,...) im Byte-Modus (Version 1–12: 21 + 4*(V-1) Module)
const QR_Q_BYTE_KAPAZITAET = [11, 20, 32, 46, 60, 74, 86, 108, 130, 151, 177, 203];
export function qrModuleFuerDaten(daten) {
  const n = String(daten || '').length;
  for (let v = 0; v < QR_Q_BYTE_KAPAZITAET.length; v++) {
    if (n <= QR_Q_BYTE_KAPAZITAET[v]) return Math.max(29, 21 + 4 * v);
  }
  return 69;
}

// 🖼️ Saison-Thema oder eigenes Bild (z. B. aus ChatGPT / Shop-Logo) als 1-Bit-ZPL-Grafik (^GFA) unter dem Kasten
const GRAFIK_KEY = 'kiosk_label_grafik';
export const LABEL_THEMEN = ['keine', 'halloween', 'weihnachten', 'festtage', 'herz'];
const presetCache = {};

function erstellePresetGrafik(thema) {
  if (presetCache[thema]) return presetCache[thema];
  const bpr = 64, w = bpr * 8, h = 72; // 512 × 72 Punkte (~63 × 9 mm)
  const buf = new Uint8Array(bpr * h);
  const pkt = (x, y, an = true) => {
    const xi = Math.round(x), yi = Math.round(y);
    if (xi < 0 || xi >= w || yi < 0 || yi >= h) return;
    const idx = yi * bpr + (xi >> 3), bit = 0x80 >> (xi & 7);
    if (an) buf[idx] |= bit; else buf[idx] &= ~bit;
  };
  const kreis = (cx, cy, r, an = true) => {
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) if (dx * dx + dy * dy <= r * r) pkt(cx + dx, cy + dy, an);
  };
  const ring = (cx, cy, r, d = 3, an = true) => {
    const rIn = Math.max(0, r - d);
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const q = dx * dx + dy * dy;
      if (q <= r * r && q >= rIn * rIn) pkt(cx + dx, cy + dy, an);
    }
  };
  const linie = (x0, y0, x1, y1, d = 2, an = true) => {
    const n = Math.max(1, Math.hypot(x1 - x0, y1 - y0));
    for (let i = 0; i <= n; i++) kreis(x0 + (x1 - x0) * (i / n), y0 + (y1 - y0) * (i / n), d / 2, an);
  };
  const dreieck = (cx, yOben, yUnten, halbeBreite, an = true) => {
    const dh = Math.max(1, yUnten - yOben);
    for (let y = yOben; y <= yUnten; y++) {
      const hw = Math.round(halbeBreite * ((y - yOben) / dh));
      for (let x = cx - hw; x <= cx + hw; x++) pkt(x, y, an);
    }
  };
  const stern = (cx, cy, r) => {
    linie(cx - r, cy, cx + r, cy, 2.5);
    linie(cx, cy - r, cx, cy + r, 2.5);
    const d = Math.round(r * 0.68);
    linie(cx - d, cy - d, cx + d, cy + d, 2);
    linie(cx - d, cy + d, cx + d, cy - d, 2);
  };
  // Zierlinien links und rechts
  linie(16, 36, 86, 36, 3); linie(426, 36, 496, 36, 3);
  stern(52, 20, 7); stern(460, 20, 7);

  if (thema === 'halloween') {
    const kuerbis = (cx, cy, r) => {
      for (const ox of [-r * 0.45, 0, r * 0.45]) kreis(cx + ox, cy, r * 0.88, true);
      for (let y = cy - r - 6; y <= cy - r + 2; y++) for (let x = cx - 3; x <= cx + 3; x++) pkt(x, y, true);
      dreieck(cx - r * 0.38, cy - r * 0.38, cy - 2, r * 0.24, false);
      dreieck(cx + r * 0.38, cy - r * 0.38, cy - 2, r * 0.24, false);
      dreieck(cx, cy - 3, cy + 4, 4, false);
      for (let dx = -r * 0.62; dx <= r * 0.62; dx++) {
        const my = cy + r * 0.28 + Math.round(Math.cos((dx / (r * 0.62)) * Math.PI) * 5);
        for (let t = -3; t <= 3; t++) pkt(cx + dx, my + t, false);
      }
      for (const zx of [-5, 5]) for (let y = cy + 6; y <= cy + 12; y++) for (let x = cx + zx - 2; x <= cx + zx + 2; x++) pkt(x, y, true);
    };
    const fledermaus = (cx, cy) => {
      kreis(cx, cy, 5, true);
      dreieck(cx - 3, cy - 9, cy - 3, 2, true); dreieck(cx + 3, cy - 9, cy - 3, 2, true);
      for (const s of [-1, 1]) {
        linie(cx, cy, cx + s * 22, cy - 8, 4);
        linie(cx + s * 22, cy - 8, cx + s * 28, cy + 3, 3);
        linie(cx + s * 8, cy + 3, cx + s * 22, cy - 3, 3);
      }
    };
    kuerbis(136, 40, 24); fledermaus(196, 28); kuerbis(256, 38, 27); fledermaus(316, 28); kuerbis(376, 40, 24);
  } else if (thema === 'weihnachten') {
    const baum = (cx, yFuss, scl = 1) => {
      stern(cx, yFuss - Math.round(54 * scl), Math.round(6 * scl));
      dreieck(cx, yFuss - Math.round(46 * scl), yFuss - Math.round(28 * scl), Math.round(14 * scl), true);
      dreieck(cx, yFuss - Math.round(34 * scl), yFuss - Math.round(14 * scl), Math.round(20 * scl), true);
      dreieck(cx, yFuss - Math.round(20 * scl), yFuss - Math.round(2 * scl), Math.round(26 * scl), true);
      for (let y = yFuss - 2; y <= yFuss + 5; y++) for (let x = cx - 4; x <= cx + 4; x++) pkt(x, y, true);
      kreis(cx - 6, yFuss - Math.round(10 * scl), 2.5, false);
      kreis(cx + 7, yFuss - Math.round(18 * scl), 2.5, false);
      kreis(cx - 3, yFuss - Math.round(26 * scl), 2, false);
    };
    const flocke = (cx, cy, r) => {
      for (let k = 0; k < 6; k++) {
        const w = (k * Math.PI) / 3, dx = Math.cos(w), dy = Math.sin(w);
        linie(cx, cy, cx + dx * r, cy + dy * r, 2.5);
        const mx = cx + dx * (r * 0.6), my = cy + dy * (r * 0.6);
        for (const s of [-1, 1]) linie(mx, my, mx + Math.cos(w + s * 0.7) * (r * 0.35), my + Math.sin(w + s * 0.7) * (r * 0.35), 2);
      }
    };
    baum(134, 62, 0.92); flocke(195, 36, 15); baum(256, 63, 1.05); flocke(317, 36, 15); baum(378, 62, 0.92);
  } else if (thema === 'festtage') {
    const geschenk = (cx, cy, r) => {
      for (let y = cy - r + 6; y <= cy + r; y++) for (let x = cx - r; x <= cx + r; x++) pkt(x, y, true);
      for (let y = cy - r + 6; y <= cy + r; y++) for (let x = cx - 3; x <= cx + 3; x++) pkt(x, y, false);
      for (let y = cy - 1; y <= cy + 4; y++) for (let x = cx - r; x <= cx + r; x++) pkt(x, y, false);
      ring(cx - 7, cy - r + 1, 7, 3, true); ring(cx + 7, cy - r + 1, 7, 3, true);
    };
    stern(124, 36, 16); geschenk(188, 42, 20); stern(256, 34, 22); geschenk(324, 42, 20); stern(388, 36, 16);
    stern(155, 16, 7); stern(357, 16, 7);
  } else if (thema === 'herz') {
    const herz = (cx, cy, s) => {
      kreis(cx - s * 0.5, cy - s * 0.3, s * 0.58, true);
      kreis(cx + s * 0.5, cy - s * 0.3, s * 0.58, true);
      dreieck(cx, cy + s * 0.95, cy - s * 0.1, s * 1.04, false); // Clear below first if needed
      for (let y = Math.round(cy - s * 0.15); y <= Math.round(cy + s * 0.95); y++) {
        const t = (y - (cy - s * 0.15)) / Math.max(1, s * 1.1);
        const hw = Math.round(s * 1.02 * (1 - t));
        for (let x = cx - hw; x <= cx + hw; x++) pkt(x, y, true);
      }
    };
    // Smiley in der Mitte
    ring(256, 36, 26, 4, true);
    kreis(246, 29, 3.5, true); kreis(266, 29, 3.5, true);
    for (let a = 0.25; a <= Math.PI - 0.25; a += 0.05) kreis(256 + Math.cos(a) * 14, 34 + Math.sin(a) * 12, 2, true);
    herz(176, 36, 18); herz(336, 36, 18);
    stern(124, 36, 11); stern(388, 36, 11);
  }

  let hex = '';
  for (let i = 0; i < buf.length; i++) hex += buf[i].toString(16).toUpperCase().padStart(2, '0');
  const res = { bpr, h, hex };
  presetCache[thema] = res;
  return res;
}

export function parseLabelGrafik(text) {
  const s = String(text || '').trim();
  const mPreset = /^preset:(halloween|weihnachten|festtage|herz)$/.exec(s);
  if (mPreset) return { art: mPreset[1], ...erstellePresetGrafik(mPreset[1]) };
  const mCustom = /^custom:(\d{1,2}):(\d{1,3}):([0-9A-F]{32,38400})$/.exec(s);
  if (mCustom) {
    const bpr = +mCustom[1], h = +mCustom[2], hex = mCustom[3];
    if (bpr >= 8 && bpr <= 96 && h >= 8 && h <= 240 && hex.length === bpr * h * 2) return { art: 'eigen', bpr, h, hex };
  }
  return { art: 'keine', bpr: 0, h: 0, hex: '' };
}

export function formatLabelGrafik(g) {
  if (!g || !g.art || g.art === 'keine') return 'keine';
  if (LABEL_THEMEN.includes(g.art) && g.art !== 'keine') return `preset:${g.art}`;
  if (g.art === 'eigen' && g.bpr >= 8 && g.bpr <= 96 && g.h >= 8 && g.h <= 240 && /^[0-9A-F]+$/.test(g.hex || '') && g.hex.length === g.bpr * g.h * 2) {
    return `custom:${g.bpr}:${g.h}:${g.hex}`;
  }
  return 'keine';
}

export function getLabelGrafik() {
  try { return parseLabelGrafik(localStorage.getItem(GRAFIK_KEY)); } catch { return parseLabelGrafik('keine'); }
}
export function setLabelGrafik(g) {
  try { localStorage.setItem(GRAFIK_KEY, formatLabelGrafik(g)); } catch { /* */ }
}

/**
 * Wandelt ein hochgeladenes Bild (Datei / Data-URL / Image) im Browser in eine 1-Bit-ZPL-Grafik (^GFA) um.
 * Schneidet weiße Ränder automatisch weg und unterstützt Strichzeichnung (Schwellenwert) sowie Foto-Raster (Floyd-Steinberg).
 */
export async function bildZuLabelGrafik(quelle, opts = {}) {
  const stil = opts.stil === 'raster' ? 'raster' : 'strich';
  const schwelle = Number.isFinite(opts.schwelle) ? opts.schwelle : 150;
  const invert = !!opts.invert;
  const maxH = { klein: 72, mittel: 112, gross: 160 }[opts.groesse] || 112;
  const maxW = 512; // 64 Bytes je Zeile

  const img = await new Promise((resolve, reject) => {
    if (typeof HTMLImageElement !== 'undefined' && quelle instanceof HTMLImageElement) return resolve(quelle);
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => { uiText('ui.svc.imageLoadFailed', 'Bild konnte nicht geladen werden').then((m) => reject(new Error(m))); };
    if (typeof File !== 'undefined' && quelle instanceof Blob) {
      const r = new FileReader();
      r.onload = () => { el.src = r.result; };
      r.onerror = () => { uiText('ui.svc.fileReadFailed', 'Datei konnte nicht gelesen werden').then((m) => reject(new Error(m))); };
      r.readAsDataURL(quelle);
    } else {
      el.src = String(quelle || '');
    }
  });

  // 1. Auf Arbeits-Canvas zeichnen und weiße Ränder ringsum abschneiden
  const rohW = Math.min(800, img.naturalWidth || img.width || 200);
  const rohH = Math.min(800, Math.max(1, Math.round(((img.naturalHeight || img.height || 100) * rohW) / Math.max(1, img.naturalWidth || img.width || 200))));
  const c0 = document.createElement('canvas');
  c0.width = rohW; c0.height = rohH;
  const x0 = c0.getContext('2d');
  x0.fillStyle = '#fff'; x0.fillRect(0, 0, rohW, rohH);
  x0.drawImage(img, 0, 0, rohW, rohH);
  const d0 = x0.getImageData(0, 0, rohW, rohH).data;
  const grau = (x, y) => {
    const i = (y * rohW + x) * 4, a = d0[i + 3] / 255;
    const g = (d0[i] * 0.299 + d0[i + 1] * 0.587 + d0[i + 2] * 0.114) * a + 255 * (1 - a);
    return invert ? 255 - g : g;
  };
  let minX = rohW, minY = rohH, maxX = -1, maxY = -1;
  for (let y = 0; y < rohH; y++) for (let x = 0; x < rohW; x++) {
    if (grau(x, y) < 238) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX || maxY < minY) { minX = 0; minY = 0; maxX = rohW - 1; maxY = rohH - 1; }
  const cw = Math.max(1, maxX - minX + 1), ch = Math.max(1, maxY - minY + 1);

  // 2. Proportional auf Zielgröße (max. 512 × maxH) skalieren
  const faktor = Math.min(maxW / cw, maxH / ch);
  const zielW = Math.max(16, Math.min(maxW, Math.round(cw * faktor)));
  const zielH = Math.max(16, Math.min(maxH, Math.round(ch * faktor)));
  const bpr = Math.max(8, Math.ceil(zielW / 8));
  const canvasW = bpr * 8;
  const c1 = document.createElement('canvas');
  c1.width = canvasW; c1.height = zielH;
  const x1 = c1.getContext('2d');
  x1.fillStyle = '#fff'; x1.fillRect(0, 0, canvasW, zielH);
  x1.drawImage(c0, minX, minY, cw, ch, Math.floor((canvasW - zielW) / 2), 0, zielW, zielH);
  const d1 = x1.getImageData(0, 0, canvasW, zielH).data;
  const lum = new Float32Array(canvasW * zielH);
  for (let i = 0; i < lum.length; i++) {
    const p = i * 4, a = d1[p + 3] / 255;
    const g = (d1[p] * 0.299 + d1[p + 1] * 0.587 + d1[p + 2] * 0.114) * a + 255 * (1 - a);
    lum[i] = invert ? 255 - g : g;
  }

  // 3. 1-Bit-Umwandlung (Strichzeichnung vs. Floyd-Steinberg-Raster)
  if (stil === 'raster') {
    for (let y = 0; y < zielH; y++) {
      for (let x = 0; x < canvasW; x++) {
        const idx = y * canvasW + x, alt = lum[idx], neu = alt < schwelle ? 0 : 255, err = alt - neu;
        lum[idx] = neu;
        if (x + 1 < canvasW) lum[idx + 1] += err * (7 / 16);
        if (y + 1 < zielH) {
          if (x > 0) lum[idx + canvasW - 1] += err * (3 / 16);
          lum[idx + canvasW] += err * (5 / 16);
          if (x + 1 < canvasW) lum[idx + canvasW + 1] += err * (1 / 16);
        }
      }
    }
  }

  let hex = '';
  for (let y = 0; y < zielH; y++) {
    for (let b = 0; b < bpr; b++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) if (lum[y * canvasW + b * 8 + bit] < schwelle) byte |= 0x80 >> bit;
      hex += byte.toString(16).toUpperCase().padStart(2, '0');
    }
  }
  return { art: 'eigen', bpr, h: zielH, hex };
}

/** Alle Etikett-Einstellungen dieses Geräts auf einmal (auch für die Vorschau im Etikett-Dialog). */
export function getLabelCfg() {
  return { lang: getPrinterLang(), layout: getLabelLayout(), size: getLabelSize(), rahmen: isLabelFrameOn(),
    kopf: getLabelKopf(), zeilen: getLabelZeilen(), dienst: isLabelCarrierOn(),
    rand: getLabelRand(), vintedNr: isVintedSortOn(), code: getLabelCode(),
    gruss: getLabelGruss(), grafik: getLabelGrafik(),
    qrUrl: getLabelQrUrl(), qrText: getLabelQrText() };
}
/** Etikett-Einstellungen aus dem Etikett-Dialog an diesem Gerät speichern. */
export function saveLabelCfg(cfg) {
  setPrinterLang(cfg.lang);
  setLabelLayout(cfg.layout);
  setLabelSize(cfg.size);
  setLabelFrameOn(!!cfg.rahmen);
  setLabelKopf(cfg.kopf);
  setLabelZeilen(cfg.zeilen);
  setLabelCarrierOn(!!cfg.dienst);
  if (cfg.code) setLabelCode(cfg.code);
  if (cfg.rand) setLabelRand(cfg.rand);
  if (cfg.gruss !== undefined) setLabelGruss(cfg.gruss);
  if (cfg.grafik !== undefined) setLabelGrafik(cfg.grafik);
  if (cfg.qrUrl !== undefined) setLabelQrUrl(cfg.qrUrl);
  if (cfg.qrText !== undefined) setLabelQrText(cfg.qrText);
  if (cfg.layout === 'nachname') setAutoRegalLetter(true); // wie bisher beim Wählen dieses Layouts
  try { if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') window.dispatchEvent(new CustomEvent('kiosk:label-cfg-changed')); } catch { /* */ }
}

// 🔤 Fach automatisch = Anfangsbuchstabe des Sortier-Nachnamens (Sortierung nach Nachname)
const AUTO_LETTER_KEY = 'kiosk_regal_auto_letter';
export function isAutoRegalLetter() {
  if (!hasCustomFeature('nachnameSortierung')) return false; // Sonderfunktion (Konto)
  try { return localStorage.getItem(AUTO_LETTER_KEY) === '1'; } catch { return false; }
}
export function setAutoRegalLetter(on) {
  try { localStorage.setItem(AUTO_LETTER_KEY, on ? '1' : '0'); } catch { /* */ }
}

// 🚚 Pro Paketdienst abweichende Sortierung (z. B. 'postnl:hausnummer;vinted:kurznummer')
const CARRIER_SORT_KEY = 'kiosk_carrier_sort';
export const CARRIER_SORT_MODES = ['hausnummer', 'nachname', 'kurznummer'];

export function parseCarrierSort(raw) {
  const out = {};
  if (!raw || raw === 'keine' || typeof raw !== 'string') return out;
  for (const part of raw.split(';')) {
    const m = /^([a-z0-9_-]{2,24}):(hausnummer|nachname|kurznummer)$/.exec(part.trim());
    if (m) out[m[1]] = m[2];
  }
  return out;
}

export function formatCarrierSort(map) {
  if (!map || typeof map !== 'object') return 'keine';
  const parts = [];
  for (const [k, v] of Object.entries(map)) {
    const id = String(k || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24);
    if (id.length >= 2 && CARRIER_SORT_MODES.includes(v)) {
      parts.push(`${id}:${v}`);
    }
  }
  return parts.length ? parts.slice(0, 20).join(';') : 'keine';
}

export function getCarrierSortMap() {
  try {
    const map = parseCarrierSort(localStorage.getItem(CARRIER_SORT_KEY));
    if (!map.vinted && localStorage.getItem('kiosk_vinted_sort') === '1') {
      map.vinted = 'kurznummer';
    }
    return map;
  } catch { return {}; }
}

export function setCarrierSortMap(map) {
  try {
    const formatted = formatCarrierSort(map);
    localStorage.setItem(CARRIER_SORT_KEY, formatted);
    if (map && map.vinted) {
      localStorage.setItem('kiosk_vinted_sort', map.vinted === 'kurznummer' ? '1' : '0');
    }
  } catch { /* */ }
}

/** Normalisiert Paketdienst-Namen („PostNL", „DHL Express", „Vinted Go" …) auf die Profil-ID ('postnl', 'dhl', 'vinted' …). */
export function normalizeCarrierId(paketdienst, tracking = '') {
  const raw = String(paketdienst || '').trim() || guessCarrier(tracking, '');
  const s = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!s) {
    if (vintedCodeAusTracking(tracking)) return 'vinted';
    return '';
  }
  if (s.includes('postnl') || s === 'tntpost') return 'postnl';
  if (s.includes('grenzpaket')) return 'grenzpaket';
  if (s.includes('vinted') || s.includes('homerr')) return 'vinted';
  if (s.includes('mondial')) return 'mondialrelay';
  if (s.includes('inpost')) return 'inpost';
  if (s.includes('dhlexpress')) return 'dhlexpress';
  if (s.includes('dhl') || s.includes('deutschepost')) return 'dhl';
  if (s.includes('hermes') || s.includes('evri')) return 'hermes';
  if (s.includes('dpd')) return 'dpd';
  if (s.includes('gls')) return 'gls';
  if (s.includes('ups')) return 'ups';
  if (s.includes('amazon')) return 'amazon';
  if (s.includes('fedex') || s.includes('tnt')) return 'fedex';
  return s.slice(0, 24);
}

/** Hat dieser Paketdienst eine vom Standard abweichende (explizit gesetzte) Sortier-Regel? ('hausnummer' | 'nachname' | 'kurznummer' | null) */
export function getExplicitCarrierSort(paketdienst, trackingNumber = '') {
  const cid = normalizeCarrierId(paketdienst, trackingNumber);
  if (!cid) return null;
  const map = getCarrierSortMap();
  if (map[cid]) return map[cid];
  if (cid === 'dhlexpress' && map.dhl) return map.dhl;
  return null;
}

/**
 * Effektive Sortierung für ein Paket (anhand Paketdienst / Sendungsnummer):
 *   - Wenn für den erkannten Paketdienst eine eigene Regel hinterlegt ist → diese ('hausnummer' | 'nachname' | 'kurznummer')
 *   - Sonst der globale Shop-Standard ('nachname' wenn Nachname-Sortierung aktiv, sonst 'hausnummer').
 */
export function getCarrierSortMode(paketdienst, trackingNumber = '', fallbackAutoLetter = null) {
  const explicit = getExplicitCarrierSort(paketdienst, trackingNumber);
  if (explicit) return explicit;
  const globalNachname = fallbackAutoLetter !== null
    ? !!fallbackAutoLetter
    : (isAutoRegalLetter() || getLabelLayout() === 'nachname');
  return globalNachname ? 'nachname' : 'hausnummer';
}

export function isAutoPrintEnabled() {
  // Standard: AN. Wer einen Drucker einrichtet, will drucken — sonst müsste er
  // den Schalter erst suchen. Nur ein ausdrückliches '0' schaltet den Auto-Druck ab.
  // (Ohne eingerichteten Drucker passiert trotzdem nichts — die Aufrufer prüfen die Config.)
  return localStorage.getItem(AUTOPRINT_KEY) !== '0';
}

export function setAutoPrintEnabled(enabled) {
  localStorage.setItem(AUTOPRINT_KEY, enabled ? '1' : '0');
}

// ============================================================
// Geräte
// ============================================================

// Prüft, ob Bluetooth am Gerät eingeschaltet ist.
export async function isBluetoothEnabled() {
  if (!isPrinterSupported()) return false;
  try {
    const res = await bt().isEnabled();
    return !!res?.enabled;
  } catch {
    return false;
  }
}

// Fordert die Bluetooth-Berechtigung ("Geräte in der Nähe") explizit an.
// Gibt true zurück, wenn erteilt.
export async function requestBtPermission() {
  if (!isPrinterSupported()) return false;
  try {
    const res = await bt().requestPermissions();
    return res?.btconnect === 'granted';
  } catch {
    return false;
  }
}

export async function listPairedPrinters() {
  if (!isPrinterSupported()) throw new Error(await uiText('ui.svc.printerAndroidOnly', 'Drucker nur unter Android verfügbar'));
  await requestBtPermission(); // Dialog anzeigen, falls noch nicht erteilt
  const res = await bt().listPaired();
  return res?.devices || [];
}

// ============================================================
// ESC/POS Label-Erzeugung
// ============================================================

// Umlaute transliterieren — die Standard-Codepage vieler Thermodrucker hat
// keine deutschen Sonderzeichen, sonst kommt Müll raus.
function transliterate(str) {
  return String(str || '')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss');
}

function bytesOf(str) {
  const clean = transliterate(str);
  const out = [];
  for (let i = 0; i < clean.length; i++) {
    const c = clean.charCodeAt(i);
    out.push(c < 256 ? c : 0x3f); // alles Nicht-Latin1 -> '?'
  }
  return out;
}

// Extrahiert die Hausnummer aus "Straße 12a" -> "12a", auch bei PostNL-Klammerformaten
// wie "3062EP, 24A", "3062EP 24-A" -> "24A", "3052SL, 198 A02" -> "198" oder "Uitweg 72 02" -> "72".
export function extractHausnummer(streetAndNumber) {
  let s = String(streetAndNumber || '').replace(/[()]/g, ' ').trim();
  if (!s) return '';
  // Unvollständige / alleinstehende NL-PLZ ohne Hausnummer („3051S", „Nr. 3051S", „3051SP") ist KEINE Hausnummer
  if (/^(?:nr\.?\s*)?\d{4}\s*[a-zA-Z]{1,2}$/i.test(s)) return '';
  // Niederländische PLZ am Anfang („3062EP, 24A", „3045 ER 34") abtrennen
  s = s.replace(/^\d{4}\s*[a-zA-Z]{2}\b[\s,;-]*/, '').trim();
  if (!s) return '';
  // Wohnungs-/Etagenzusatz nach Hausnummer („198 A02", „12 H01", „Uitweg 72 02") abtrennen, damit die echte Hausnummer bleibt
  s = s.replace(/(\b\d+\s*-?\s*[a-zA-Z]?)\s+(?:[a-zA-Z]{1,3}\d{1,4}|\d{1,4}[a-zA-Z]?)\s*$/, '$1').trim();
  // … auch direkt angehängt („79K019", „38b01", „26a02" — NL-Toevoeging, Folkert 21.10.2026: Etikett zeigte „019" statt 79)
  s = s.replace(/(\b\d+)\s*-?\s*[a-zA-Z]{1,3}\d{2,4}\s*$/, '$1').trim();
  const m = s.match(/(\d+\s*-?\s*[a-zA-Z]?)\s*$/);
  return m ? m[1].replace(/[\s-]+/g, '') : '';
}

// Erkennt Weiterleitungs-/Filialadressen, bei denen es keine echte Hausnummer
// gibt (Postfiliale/Packstation). Dann soll NICHT die "Hausnummer" gedruckt
// werden, sondern die Initialen. Beispiele: "Postfiliale 553", "xxx553", "553".
export function isForwardingAddress(streetAndNumber) {
  const s = String(streetAndNumber || '').trim();
  if (!s) return false;
  // Sonderfall: die "Hausnummer" 553 gibt es nicht — das ist immer eine
  // Weiterleitung (Postfiliale). Egal was sonst dasteht → Initialen.
  // (^|\D)553(\D|$) = 553 als eigenständige Zahl, nicht Teil von 5531/1553.
  if (/(^|\D)553(\D|$)/.test(s)) return true;
  if (/postfiliale|packstation|filiale|paketshop|postnummer|post\s*nr/i.test(s)) return true;
  // Straßenname = alles vor der abschließenden Nummer
  const nameOnly = s.replace(/(\d+\s*-?\s*[a-zA-Z]?)\s*$/, '').trim();
  if (!nameOnly) return true; // nur eine Nummer ohne Straße („12") = keine echte Hausnummer → Initialen (wie bisher)
  if (/^x+$/i.test(nameOnly)) return true; // Platzhalter wie "xxx553"
  return false;
}

// Bereinigt einen (oft per OCR verschmutzten) Namen:
//   "´´ für Max Mustermann Bobolution GmbH" -> "Max Mustermann Bobolution GmbH"
// Entfernt führenden Müll und nimmt bei "... für/z.Hd./c/o <Name>" den Empfänger.
export function cleanName(raw) {
  let s = String(raw || '').replace(/\s+/g, ' ').trim();
  if (/^#\d+/.test(s)) return s; // 🔢 Laufnummer (#001) für Gepäck/Garderobe unverändert erhalten
  s = s.replace(/^[^\p{L}\p{N}]+/u, ''); // führende Nicht-Buchstaben/Ziffern (´´, ", ', …)
  const m = s.match(/(?:\bf(?:ü|ue)r\b|\bz\.?\s*hd\.?\b|\bc\/o\b)\s*:?\s*(.+)$/i);
  if (m && m[1].trim().length >= 2) s = m[1].trim();
  return s.replace(/^[^\p{L}\p{N}]+/u, '').replace(/\s+/g, ' ').trim();
}

// Initialen aus dem Namen, max. 2 Buchstaben: "Max Mustermann" -> "MM" (bei Laufnummer "#001" -> "#001")
export function getInitials(name) {
  const parts = cleanName(name).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (/^#\d+$/.test(parts[0])) return parts[0];
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

export function isDoubleLabelEnabled() {
  try { return localStorage.getItem('kiosk_double_label') === '1'; } catch { return false; }
}

// Anzahl zu druckender Etiketten für ein Paket (Mehrfach-Pakete + optionaler 2. Kunden-Abholbon)
export function getCopies(paket) {
  if (paket?.palettenTyp) {
    return isDoubleLabelEnabled() ? 2 : 1;
  }
  const n = parseInt(paket?.paketAnzahl, 10);
  // Ab >5 Paketen (z. B. Großlieferung / Palettenware bis 999) 1 Sammeletikett drucken statt bis zu 999 Einzeletiketten
  const base = n && n > 1 && n <= 5 ? n : 1;
  return isDoubleLabelEnabled() ? base + 1 : base;
}

export function formatCopyLine(prefix, index, total) {
  if (!total || total <= 1) return '';
  if (isDoubleLabelEnabled()) {
    if ((index || 1) >= total) return 'ABHOLBON / RECEIPT';
    if (total === 2) return '';
    return `${prefix} ${index || 1}/${total - 1}`;
  }
  return `${prefix} ${index || 1}/${total}`;
}

/**
 * Extrahiert die Bluetooth-MAC-Adresse aus einem Bixolon-QR-Code oder einer
 * direkt eingegebenen Adresse.
 *   "DHGWRKA12120556-B2-74F07DE0785F" -> "74:F0:7D:E0:78:5F"
 *   "74:F0:7D:E0:78:5F"               -> "74:F0:7D:E0:78:5F"
 * @returns {{address: string}|null}
 */
export function parsePrinterCode(text) {
  if (!text) return null;
  const cleaned = String(text).trim();

  // 🌐 Netzwerkdrucker: IPv4 (optional :Port) → tcp://ip:port
  const ip = cleaned.match(/^(?:tcp:\/\/)?((?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3})(?::(\d{2,5}))?$/i);
  if (ip) return { address: `tcp://${ip[1]}:${ip[2] || 9100}`, network: true };

  // Schon als MAC mit Doppelpunkten/Bindestrichen?
  const macSep = cleaned.match(/([0-9A-Fa-f]{2}([:-][0-9A-Fa-f]{2}){5})/);
  if (macSep) {
    return { address: macSep[1].replace(/-/g, ':').toUpperCase() };
  }

  // Sonst: letzte 12 Hex-Zeichen (die MAC steht am Ende des QR-Inhalts)
  const lastSeg = cleaned.split(/[-_\s]/).pop() || '';
  let hex = /^[0-9A-Fa-f]{12}$/.test(lastSeg) ? lastSeg : '';
  if (!hex) {
    const m = cleaned.replace(/[^0-9A-Fa-f]/g, '').match(/([0-9A-Fa-f]{12})$/);
    if (m) hex = m[1];
  }
  if (!hex) return null;
  return { address: hex.toUpperCase().match(/.{2}/g).join(':') };
}

function formatDatum(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE');
}

// ── Block-Rendering-Parameter (am echten Drucker eingestellt) ─────────────
const BLOCK_BYTE = 0xDB;   // CP437 Vollblock — füllt die Zeichenzelle
const CELL_W     = 7;      // ungefähre Zellbreite in Dots (für Seitenverhältnis)
const LINE_H     = 12;     // Zeilenabstand in Dots (eng = satter)
const MAX_COLS   = 46;     // max. Spalten = ~volle Etikettbreite minus Rand
const LEFT_MARGIN = 12;    // linker Rand in Dots (sonst links abgeschnitten)
const TOP_MARGIN  = 24;    // oben 2 Zeilen Platz lassen
const BIG_OVERPRINT   = [[0, 0], [1, 0], [0, 1], [1, 1]]; // 4-fach = sehr fett
const SMALL_OVERPRINT = [[0, 0], [1, 0]];                 // 2-fach = fett
const SMALL_SIZE = 3;      // Größe der normalen Schrift für den Kleintext
const SMALL_LH   = 32;     // Zeilenabstand für den Kleintext (Größe 3, kompakt)
const MED_SIZE   = 6;      // Font 0, Größe 6 — größere Schrift für Name + Straße/Hausnummer
const MED_LH     = 56;     // Zeilenabstand für die größere Schrift
const FONT_FAMILY = 'sans-serif-condensed, sans-serif';   // schmal -> kleinere Lücken

/**
 * Rendert Text als Block-Grafik (Vollblock-Zeichen 0xDB), gezeichnet auf ein
 * Canvas und in ein Raster abgetastet. So entsteht im CPCL-Modus beliebig große,
 * lesbare Schrift — ohne SETMAG/Grafikbefehle (die diese Firmware nicht kann).
 *
 * @param {string} text
 * @param {{fillCols?: number, fixedRows?: number, fontPx?: number}} cfg
 *   fillCols  → füllt genau so viele Spalten (für die große Kennung)
 *   fixedRows → feste Zeilenhöhe, Breite ergibt sich aus dem Text (für Kleintext)
 * @returns {{rows: number[][], cols: number}}
 */
function renderBlock(text, cfg = {}) {
  const fontPx = cfg.fontPx || 64;
  const canvas = document.createElement('canvas');
  canvas.width = 900;
  canvas.height = Math.ceil(fontPx * 1.7);
  const ctx = canvas.getContext('2d');
  ctx.font = `bold ${fontPx}px ${FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000';
  ctx.fillText(text, 0, 2);

  const W = canvas.width, H = canvas.height;
  const data = ctx.getImageData(0, 0, W, H).data;
  const dark = (x, y) => {
    const i = (y * W + x) * 4;
    return (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) < 128;
  };

  // Tinten-Bounding-Box (unabhängig von Font-Metriken)
  let minX = W, minY = H, maxX = -1, maxY = -1;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (dark(x, y)) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { rows: [], cols: 0 };

  const bw = maxX - minX + 1, bh = maxY - minY + 1;
  let cols, rows;
  if (cfg.fillCols) {
    cols = cfg.fillCols;
    rows = Math.max(1, Math.round(cols * CELL_W * bh / (bw * LINE_H)));
    // ⛔ Höhen-Deckel greift AUCH hier (fillCols): Eine EINSTELLIGE Hausnummer („1") ist
    // schmal und würde auf volle Breite gestreckt riesig hoch. Auf maxRows begrenzen und die
    // Breite ans Seitenverhältnis anpassen → gleiche Höhe wie eine zweistellige Nummer, nur
    // natürlich schmaler (statt in die Breite gezerrt). War vorher nur im else-Zweig → Bug.
    const maxRows = cfg.maxRows;
    if (maxRows && rows > maxRows) {
      rows = maxRows;
      cols = Math.max(1, Math.min(cfg.fillCols, Math.round(rows * LINE_H * bw / (CELL_W * bh))));
    }
  } else {
    rows = cfg.fixedRows || 6;
    cols = Math.round(rows * LINE_H * bw / (CELL_W * bh));
    if (cols > MAX_COLS) { // zu breit -> auf Etikettbreite begrenzen, Höhe anpassen
      cols = MAX_COLS;
      rows = Math.max(1, Math.round(cols * CELL_W * bh / (bw * LINE_H)));
    }
    // ⛔ Höhen-Deckel: SEHR kurze Kennungen (einstellige Hausnummer, „7") sind schmal —
    // auf volle Breite gestreckt würden sie riesig hoch (~50 Zeilen ≈ 7 cm). Höhe auf das
    // Niveau einer zweistelligen Nummer begrenzen, Breite folgt dem Seitenverhältnis.
    const maxRows = cfg.maxRows || 24;
    if (rows > maxRows) {
      rows = maxRows;
      cols = Math.max(1, Math.min(MAX_COLS, Math.round(rows * LINE_H * bw / (CELL_W * bh))));
    }
  }

  const cellX = bw / cols, cellY = bh / rows;
  const out = [];
  for (let r = 0; r < rows; r++) {
    const arr = [];
    for (let c = 0; c < cols; c++) {
      const x = Math.min(maxX, minX + Math.floor(c * cellX + cellX / 2));
      const y = Math.min(maxY, minY + Math.floor(r * cellY + cellY / 2));
      arr.push(dark(x, y) ? BLOCK_BYTE : 0x20);
    }
    out.push(arr);
  }
  return { rows: out, cols };
}

/**
 * Baut ein Paket-Etikett (CPCL): Hausnummer/Initialen groß, darunter Name,
 * Regalplatz, Datum, ggf. "Paket i/n" — alles als Block-Grafik (fett).
 *
 * @param {object} paket
 * @param {{index?: number, total?: number}} [opts]
 * @returns {Uint8Array}
 */
export function buildLabelBytes(paket, opts = {}) {
  return getPrinterLang() === 'zpl' ? buildZplLabelBytes(paket, opts) : buildCpclLabelBytes(paket, opts);
}

/**
 * CPCL-Befehle für Barcode/QR der Sendungsnummer (Bixolon-Etikett 384 Dots breit). Gibt die Höhe und eine Funktion
 * zurück, die ab einer y-Position die Befehlszeilen liefert — so lässt es sich ohne Browser (Unit-Test) prüfen.
 */
export function cpclCodeBlock(paket, code = getLabelCode()) {
  const daten = code && code.art !== 'keine' ? codeDaten(paket) : '';
  const cm = { klein: { h: 50, mag: 3 }, mittel: { h: 80, mag: 4 }, gross: { h: 110, mag: 6 } }[code && code.groesse] || { h: 80, mag: 4 };
  const CPCL_W = 384;
  const bcW = daten ? code128Module(daten) : 0;
  const mitBc = !!daten && (code.art === 'barcode' || code.art === 'beide') && bcW <= CPCL_W;
  const mitQr = !!daten && (code.art === 'qr' || code.art === 'beide');
  const hoehe = (mitBc ? cm.h + 16 : 0) + (mitQr ? QR_MODULE * cm.mag + 16 : 0);
  const befehle = (y) => {
    const out = [];
    if (mitBc) { out.push(`BARCODE 128 1 1 ${cm.h} ${Math.max(0, Math.round((CPCL_W - bcW) / 2))} ${y} ${daten}\r\n`); y += cm.h + 16; }
    if (mitQr) { out.push(`BARCODE QR ${Math.max(0, Math.round((CPCL_W - QR_MODULE * cm.mag) / 2))} ${y} M 2 U ${cm.mag}\r\nMA,${daten}\r\nENDQR\r\n`); }
    return out;
  };
  return { hoehe, befehle };
}

function buildCpclLabelBytes(paket, opts = {}) {
  const gp = (getLabelLayout() === 'grenzpaket' || String(paket?.notizen || '').trim().startsWith('{"gp":1'))
    ? extractGrenzpaketLabelData(paket)
    : null;
  const hausnummer = gp?.kundenNr
    ? gp.kundenNr
    : (isForwardingAddress(paket?.streetAndNumber)
      ? '' // Postfiliale/Packstation -> keine echte Hausnummer, Initialen nutzen
      : extractHausnummer(paket?.streetAndNumber));
  const rawNameForCpcl = gp
    ? String(paket?.name || '').replace(/\(\s*\d{3,8}\s*\)/g, ' ')
    : paket?.name;
  const name = cleanName(rawNameForCpcl);
  const street = gp
    ? [gp.postboxTyp || (gp.tarif === 'Bar' ? 'Paketfach-Barzahlung' : gp.tarif === 'Kredit' ? 'Paketfach-Kredit' : 'Paketfach'), gp.paketTyp || 'Paket'].filter(Boolean).join(' | ')
    : (paket?.streetAndNumber || '').toString().trim();
  const regal = (paket?.regalplatz || '').toString().trim();
  const datum = gp ? formatGrenzpaketDatumKw(paket?.datum || paket?.updatedAt) : formatDatum(paket?.datum || paket?.updatedAt);
  const { index, total } = opts;
  // Große Kennung: Kundennummer (Grenzpaket), Hausnummer, sonst Initialen — immer GROSSBUCHSTABEN
  const gross = (hausnummer || getInitials(name) || '-').toUpperCase();

  // Höhen-Deckel nach Zeichenzahl: EINE Ziffer/Initiale ist schmal — ungedeckelt würde
  // sie auf volle Breite gestreckt riesig (≈7 cm hoch). 1 Zeichen ≈ 2 cm, sonst wie gehabt.
  const big = renderBlock(gross, { fillCols: MAX_COLS, fontPx: 72, maxRows: gross.length <= 1 ? 14 : 24 });

  // Name + Straße/Hausnummer: GRÖSSERE Schrift (Font 0, Größe 6) — auf Wunsch gut lesbar.
  const medTexts = [];
  if (name) medTexts.push(transliterate(name));
  if (street) medTexts.push(transliterate(street));
  // Rest bleibt klein: Font 7, Größe 3, fett via Überdruck
  const smallTexts = [];
  if (regal) smallTexts.push(transliterate('Regal: ' + regal));
  if (gp && (paket?.trackingNumber || gp.info)) {
    smallTexts.push(transliterate(String(paket?.trackingNumber || gp.info).slice(0, 36)));
  } else if (isLabelCarrierOn()) { // 🚚 nur wenn eingeschaltet — sonst Etikett wie bisher
    const dienst = guessCarrier(paket?.trackingNumber, paket?.paketdienst);
    if (dienst) smallTexts.push(transliterate(dienst));
  }
  if (datum) smallTexts.push(transliterate((gp ? '' : 'Eingelagert: ') + datum));
  const cpclCopyLine = formatCopyLine('Paket', index, total);
  if (cpclCopyLine) smallTexts.push(cpclCopyLine);
  // opts.zusatz (Turbo PaketPilot): „#1234" bzw. „? #1234" vor dem Grußtext — Etikett dem Paket zuordnen / prüfen
  const cpclGruss = [opts.zusatz, getLabelGruss()].filter(Boolean).join('  ');
  if (cpclGruss) smallTexts.push(transliterate(cpclGruss));

  // Code (Barcode/QR der Sendungsnummer) unten, nur wenn eingestellt (oder bei Grenzpaket immer der Paketcode)
  const gpCodePaket = gp && gp.paketCode ? { ...paket, trackingNumber: gp.paketCode } : paket;
  const codeBlock = cpclCodeBlock(gpCodePaket, gp ? { art: 'barcode', groesse: 'mittel' } : getLabelCode());
  const codeH = codeBlock.hoehe;

  const bytes = [];
  const pushAscii = (s) => { for (let i = 0; i < s.length; i++) bytes.push(s.charCodeAt(i) & 0xff); };

  // Gesamthöhe vorab berechnen
  const h = TOP_MARGIN + big.rows.length * LINE_H + 20 + medTexts.length * MED_LH + smallTexts.length * SMALL_LH + 24 + codeH;
  pushAscii(`! 0 200 200 ${h} 1\r\n`);

  // Große Kennung als Block-Grafik (4-fach überdruckt = sehr fett)
  let y = TOP_MARGIN;
  for (const [ox, oy] of BIG_OVERPRINT) {
    for (let r = 0; r < big.rows.length; r++) {
      pushAscii(`TEXT 7 0 ${LEFT_MARGIN + ox} ${y + r * LINE_H + oy} `);
      for (const b of big.rows[r]) bytes.push(b);
      bytes.push(0x0d, 0x0a);
    }
  }
  y += big.rows.length * LINE_H + 20;

  // Name + Straße: Font 0, Größe 6, 2-fach überdruckt (fett)
  for (const t of medTexts) {
    for (const [ox, oy] of SMALL_OVERPRINT) {
      pushAscii(`TEXT 0 ${MED_SIZE} ${LEFT_MARGIN + ox} ${y + oy} `);
      for (const b of bytesOf(t)) bytes.push(b);
      bytes.push(0x0d, 0x0a);
    }
    y += MED_LH;
  }

  // Restliche Kleintextzeilen: Font 7 Größe 3, 2-fach überdruckt (fett)
  for (const t of smallTexts) {
    for (const [ox, oy] of SMALL_OVERPRINT) {
      pushAscii(`TEXT 7 ${SMALL_SIZE} ${LEFT_MARGIN + ox} ${y + oy} `);
      for (const b of bytesOf(t)) bytes.push(b);
      bytes.push(0x0d, 0x0a);
    }
    y += SMALL_LH;
  }

  // Barcode/QR: der Drucker erzeugt sie selbst (CPCL BARCODE 128 bzw. BARCODE QR … ENDQR)
  for (const zeile of codeBlock.befehle(y)) pushAscii(zeile);

  pushAscii('PRINT\r\n');
  return new Uint8Array(bytes);
}

// ============================================================
// ZPL (Honeywell PC4x über ZSim, Zebra) — Etikett 10 × 15 cm, 203 dpi
// ============================================================

const ZPL_W = 812;   // 100 mm @ 203 dpi
const ZPL_H = 1218;  // 150 mm @ 203 dpi
const ZPL_M = 30;    // Rand

// ZPL-Daten: Akzente weg (nur ASCII sicher über alle ZSim-Firmwares), ^ und ~ sind Steuerzeichen.
function zplText(str) {
  return transliterate(str)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7e]/g, '?')
    .replace(/[\^~]/g, ' ');
}

function labelWords() {
  let l = 'de';
  // Wie LanguageContext: gespeicherte Wahl, sonst GERÄTESPRACHE (wer nie im Menü umstellt,
  // sieht die App z. B. niederländisch — das Etikett muss dann auch NL sein, nicht DE)
  try {
    const st = localStorage.getItem('app_language');
    l = (st || (typeof navigator !== 'undefined' && navigator.language) || 'de').slice(0, 2).toLowerCase();
  } catch { /* */ }
  if (l === 'nl') return { regal: 'Plank', vak: 'Vak', datum: 'Opgeslagen', paket: 'Pakket', von: 'Van' };
  if (l === 'en') return { regal: 'Shelf', vak: 'Shelf', datum: 'Stored', paket: 'Parcel', von: 'From' };
  return { regal: 'Regal', vak: 'Fach', datum: 'Eingelagert', paket: 'Paket', von: 'Von' };
}

// Namens-Präfixe, die beim Sortieren ignoriert werden (NL „tussenvoegsels", DE/FR/IT-Adelspartikel,
// arabisch „El"/„Al" — Folkert: „Nisrine El bouchtaoui" → Bouchtaoui)
const NAME_PREFIXES = new Set(['el', 'al', 'de', 'der', 'den', 'van', 'von', 'vom', 'het', "'t", 't', 'ter', 'ten', 'te', 'in', 'op',
  'aan', 'bij', 'uit', 'la', 'le', 'les', 'du', 'des', 'di', 'da', 'del', 'della', 'dos', 'zu', 'zum', 'zur', 'vd', 'vdr', 'vdb', 'vdv', "d'"]);
const COMPANY_RE = /\b(b\.?v\.?|v\.?o\.?f\.?|n\.?v\.?|gmbh|ug|ag|kg|e\.?k\.?|ltd|inc|llc|bvba|sarl|s\.?r\.?l\.?)\b/i;

/**
 * Sortier-Nachname nach Folkerts Regel (Green Goat): 1. Wort = Vorname, das NÄCHSTE echte
 * Wort ist der Nachname — Verbindungswörter (dos, de, van der …) werden übersprungen,
 * alles danach entfällt. Dahinter die Vornamens-Initiale.
 *   „Maria dos Santos Hernandez" → Santos (M.)     „Duane Martin Sterman" → Martin (D.)
 *   „A. de Koop" → Koop (A.)     „Koop, A. de" → Koop (A.)     „de Koop" → Koop
 * Regal-Abgleich: Ist das Fach ein Buchstabe (bzw. beginnt damit) und fängt ein Namenswort
 * damit an, gewinnt DIESES Wort — Etikett und Fach passen dann immer zusammen
 * („Anna Maria Jansen", Fach J → Jansen (A.)).
 * Firmen → ganzer Name, keine Initiale.
 */
export function splitSurname(rawName, regalHint) {
  const name = cleanName(rawName);
  if (!name) return { surname: '', initial: '', letter: '' };
  const numMatch = name.match(/^(#\d+)/);
  if (numMatch) return { surname: numMatch[1], initial: '', letter: numMatch[1] };
  const firstLetter = (s) => { const m = String(s).match(/\p{L}/u); return m ? m[0].toUpperCase() : ''; };
  // Abkuerzungen mit Punkten ("v.d.", "v.d", "vd." = van der/van den, Folkert 10.10.2026) wie das ausgeschriebene Wort behandeln
  const isPrefix = (tok) => NAME_PREFIXES.has(String(tok).toLowerCase().replace(/\./g, ''));
  const isInitial = (tok) => /^\p{L}\.?$/u.test(tok) || /^(\p{L}\.){2,}$/u.test(tok); // „A." / „A" / „J.P."
  const isDotInitial = (tok) => /^(\p{L}\.){1,3}$/u.test(tok) || /^\p{L}\.\p{L}\.?$/u.test(tok); // „F." / „F.W." / „F.W"
  if (COMPANY_RE.test(name)) {
    const tokens = name.split(/\s+/).filter(Boolean);
    // Niederländische Personal-Holdings („F.W. MULDER HOLDING B.V", „van der Vliet Beheer B.V."):
    // Stehen am Anfang Initialen mit Punkt und/oder Tussenvoegsels (van/de/van der), gefolgt von einem Nachnamen, zählt der Nachname!
    if (tokens.length >= 2 && (isDotInitial(tokens[0]) || isPrefix(tokens[0]))) {
      let idx = 0;
      while (idx < tokens.length && (isDotInitial(tokens[idx]) || isPrefix(tokens[idx]))) idx++;
      const rest = tokens.slice(idx);
      const hatNachname = rest.some((t) => !COMPANY_RE.test(t) && !/^(holding|beheer|group|groep)$/i.test(t));
      if (hatNachname && rest.length > 0) {
        const sortName = rest.join(' ');
        return { surname: sortName, initial: isDotInitial(tokens[0]) ? firstLetter(tokens[0]) : '', letter: firstLetter(rest[0]) };
      }
    }
    return { surname: name, initial: '', letter: firstLetter(name) };
  }

  let vorname = '';
  let kandidaten = []; // mögliche Nachnamen in Reihenfolge der Regel
  if (name.includes(',')) {
    const [core, rest] = name.split(',').map((x) => x.trim());
    kandidaten = core.split(/\s+/).filter((x) => x && !isPrefix(x));
    vorname = (rest || '').split(/\s+/).filter((x) => x && !isPrefix(x))[0] || '';
  } else {
    const tokens = name.split(/\s+/).filter(Boolean);
    // Einzelwort („marijanovic"): ebenfalls mit großem Anfangsbuchstaben aufs Etikett
    if (tokens.length === 1) return { surname: tokens[0].charAt(0).toUpperCase() + tokens[0].slice(1), initial: '', letter: firstLetter(tokens[0]) };
    let start = 0;
    if (!isPrefix(tokens[0])) { vorname = tokens[0]; start = 1; }
    kandidaten = tokens.slice(start).filter((x) => !isPrefix(x) && !isInitial(x));
    if (!kandidaten.length) kandidaten = [tokens[tokens.length - 1]];
  }

  let core = kandidaten[0] || name;
  const hint = firstLetter(regalHint);
  if (hint && /^\p{L}/u.test(String(regalHint || '').trim()) && firstLetter(core) !== hint) {
    const passend = kandidaten.find((k) => firstLetter(k) === hint);
    if (passend) core = passend;
  }
  // Anfangsbuchstabe groß („El bouchtaoui" → „Bouchtaoui") — Etikett soll sauber aussehen
  const gross = core ? core.charAt(0).toUpperCase() + core.slice(1) : core;
  return { surname: gross, initial: firstLetter(vorname), letter: firstLetter(core) };
}

/** Prüft, ob eine Sendungsnummer eine 10-stellige DHL-Express-Airwaybill (Waybill, Modulo-7-Prüfziffer oder XX XXXX XXXX) ist. */
export function isDhlExpressWaybill(tracking, paketdienst = '') {
  const raw = String(tracking || '').trim();
  if (!raw) return false;
  if (/\b(air)?waybill\b/i.test(raw)) return true;
  if (/^\d{2}\s+\d{4}\s+\d{4}$/.test(raw)) return true;
  const dgt = raw.replace(/\D/g, '');
  const clean = raw.replace(/[\s-]+/g, '');
  const d = String(paketdienst || '').trim().toLowerCase();
  // Nur bei leerem Dienst raten (Modulo-7 trifft 1 von 7 Zufallsnummern): sagt die KI „DHL", bleibt es DHL (Review 19.10.2026)
  if (dgt.length === 10 && dgt === clean && (d === 'dhl express' || !d)) {
    return Number(dgt.slice(0, 9)) % 7 === Number(dgt[9]);
  }
  return false;
}

/** Paketdienst NUR aus eindeutigen Sendungsnummer-Mustern — sonst leer (lieber nichts als falsch). */
export function guessCarrier(tracking, paketdienst = '') {
  const raw = String(tracking || '').trim();
  const d = String(paketdienst || '').trim();
  if (isDhlExpressWaybill(raw, d)) return 'DHL Express';
  if (d) return d;
  const t = raw.replace(/\s+/g, '').toUpperCase();
  if (!t) return '';
  // ⚠️ 3S-Nummern sind in den Niederlanden NICHT eindeutig (PostNL UND DHL Parcel) — keine Zuordnung.
  // Der Paketdienst kommt seit 30.09. vom Label-Logo (KI-Metadaten → paket.paketdienst).
  if (/^1Z[A-Z0-9]{16}$/.test(t)) return 'UPS';
  if (/^(JVGL|JJD|GM\d)/.test(t) || /^00340434\d{10,12}$/.test(t)) return 'DHL';
  return '';
}

function formatDatumZeit(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Zeichenbreiten der ZPL-Schrift 0 (CG Triumvirate Bold Condensed) bei Höhe 100, ASCII 0x20–0x7e,
// je Zeichen im Etiketten-Viewer GEMESSEN (01.10.2026) und aufgerundet. Großbuchstaben sind deutlich
// breiter als der frühere Pauschalwert 0,5 × Höhe (M 76, W 82, H/N/U 61, Bindestrich 91!) —
// „HUIJSMAN" lief über, der Drucker brach um und druckte „AN" über „HUIJSM-" (Folkert 01.10.).
const FONT0_BREITE = [30,30,48,48,48,91,61,30,30,30,48,91,30,91,30,30,48,48,48,48,48,48,48,48,48,48,30,30,100,91,100,45,91,
  56,56,54,59,50,50,59,61,28,45,56,48,76,61,58,56,58,59,54,50,61,54,82,56,56,50,30,48,30,30,50,30,
  47,50,45,50,48,28,50,50,26,26,45,26,76,50,48,50,50,34,43,28,50,45,67,45,45,39,50,50,50,30];

/** Druckbreite eines (bereits per zplText bereinigten) Textes in Punkten bei Schrifthöhe h */
export function zplTextBreite(text, h) {
  let sum = 0;
  for (const ch of String(text || '')) sum += FONT0_BREITE[ch.charCodeAt(0) - 0x20] || 61;
  return (sum * h) / 100;
}

/**
 * Umbruch wie im Drucker (^FB bricht an Leerzeichen um) bei Schrifthöhe h simulieren.
 * @returns {number} Zeilenzahl; 99, wenn ein einzelnes Wort gar nicht in die Breite passt
 */
function zplZeilen(text, h, breite) {
  const leer = zplTextBreite(' ', h);
  let zeilen = 1, lauf = 0;
  for (const wort of String(text || '').split(' ')) {
    const b = zplTextBreite(wort, h);
    if (b > breite) return 99;
    const neu = lauf ? lauf + leer + b : b;
    if (neu > breite) { zeilen++; lauf = b; } else lauf = neu;
  }
  return zeilen;
}

// Layout „Nachname groß": Nachname (Sortierbuchstabe) — Fach — Paketdienst + Datum/Uhrzeit.
// = flexibles Layout mit fester Auswahl (Ausgabe unverändert).
function buildZplSurnameLabel(paket, opts = {}, cfg = {}) {
  return buildZplFlexLabel(paket, opts, { ...cfg, kopf: 'nachname', zeilen: ['fach', 'name', 'dienst', 'datum'] });
}

/** 📦 Vinted-Kurznummer eines Pakets als „789-160" (aus der Sendungsnummer oder dem Platzhalternamen), sonst '' */
function vintedNummer(paket) {
  // auch GLS-Paket innerhalb von Vinted (Partner-Etikett, 11–12 Ziffern): die Vinted-App führt es unter den letzten
  // 6 Ziffern — ohne diese Nummer stand „Code missing" auf Folkerts Etikett (13.10.2026)
  return formatCode(paketKurzCode(paket));
}

// Was groß in der Kopfzeile steht + größte Schrifthöhe dafür
function flexKopf(paket, art, vintedNr = false) {
  const name = cleanName(paket?.name);
  const regal = (paket?.regalplatz || '').toString().trim();
  // 📦 „Vinted nach Nummer einsortieren": die Kurznummer ist das, wonach im Regal gesucht wird → groß oben
  if (vintedNr) { const nr = vintedNummer(paket); if (nr) return { text: nr, max: 190 }; }
  if (art === 'hausnummer') {
    const hn = isForwardingAddress(paket?.streetAndNumber) ? '' : extractHausnummer(paket?.streetAndNumber);
    return { text: (hn || getInitials(name) || '-').toUpperCase(), max: 300 };
  }
  if (art === 'fach') return { text: regal || '-', max: 300 };
  if (art === 'name') return { text: name || '-', max: 190 };
  if (art === 'initialen') return { text: getInitials(paket?.name) || '-', max: 300 };
  // Nachname: nur der Nachname. Die Vornamens-Initiale in Klammern („Laarakke (M.)") wurde von
  // Mitarbeitern als Fachbuchstabe gelesen (Folkert 30.09.) — der volle Name steht ohnehin darunter.
  const { surname } = splitSurname(paket?.name, paket?.regalplatz);
  return { text: surname || getInitials(paket?.name) || '-', max: 190 };
}

// Barcode (Code 128) und/oder QR-Code der Sendungsnummer unten aufs Etikett — gestapelt, zentriert. Gibt das neue y zurück.
const CODE_MASSE = { klein: { h: 60, modul: 2, mag: 4 }, mittel: { h: 100, modul: 2, mag: 6 }, gross: { h: 150, modul: 3, mag: 8 } };
function zplCodeBlock(body, y, paket, code) {
  const c = code && code.art && code.art !== 'keine' ? code : null;
  const daten = c ? codeDaten(paket) : '';
  if (!daten) return y;
  const m = CODE_MASSE[c.groesse] || CODE_MASSE.mittel;
  const inner = ZPL_W - 2 * ZPL_M;
  y += 16;
  if (c.art === 'barcode' || c.art === 'beide') {
    const module = code128Module(daten);
    let modul = m.modul;
    while (modul > 1 && module * modul > inner) modul -= 1; // zu breit → schmaler, nie über den Rand
    const w = module * modul;
    body.push(`^FO${ZPL_M + Math.max(0, Math.round((inner - w) / 2))},${y}^BY${modul},2,${m.h}^BCN,${m.h},N,N,N^FD${daten}^FS`);
    y += m.h + 16;
  }
  if (c.art === 'qr' || c.art === 'beide') {
    const px = QR_MODULE * m.mag;
    body.push(`^FO${ZPL_M + Math.max(0, Math.round((inner - px) / 2))},${y}^BQN,2,${m.mag}^FDQA,${daten}^FS`);
    y += px + 16;
  }
  return y;
}

/**
 * Mehrzeiligen Text (inkl. expliziter Zeilenumbrüche \n) für ZPL wortweise auf maxBreite umbrechen.
 * Überlange Einzelwörter (z. B. URLs) werden hart getrennt, damit keine Zeile überläuft.
 */
function umbruchAbsatzZeilen(rawText, h, maxBreite) {
  const leer = zplTextBreite(' ', h);
  const absaetze = String(rawText || '').split(/\n+/);
  const out = [];
  for (const abs of absaetze) {
    const clean = zplText(abs).replace(/\s+/g, ' ').trim();
    if (!clean) continue;
    let zeile = '', lauf = 0;
    for (const rohWort of clean.split(' ')) {
      let wort = rohWort;
      while (wort && zplTextBreite(wort, h) > maxBreite) {
        let k = wort.length - 1;
        while (k > 1 && zplTextBreite(wort.slice(0, k), h) > maxBreite) k--;
        if (zeile) { out.push(zeile); zeile = ''; lauf = 0; }
        out.push(wort.slice(0, k));
        wort = wort.slice(k);
      }
      if (!wort) continue;
      const b = zplTextBreite(wort, h);
      const neu = zeile ? lauf + leer + b : b;
      if (zeile && neu > maxBreite) {
        out.push(zeile);
        zeile = wort;
        lauf = b;
      } else {
        zeile = zeile ? `${zeile} ${wort}` : wort;
        lauf = neu;
      }
    }
    if (zeile) out.push(zeile);
  }
  return out;
}

// 💬🖼️💝 Eigener Grußtext („Have a nice day! The Green GOAT"), 1-Bit-Grafik (^GFA)
// und/oder unterer Aktionsblock (großer QR-Code + mehrzeiliger Spenden-/Infotext) unter dem Kasten.
function zplUnterKastenBlock(body, y, cfg = {}) {
  const gruss = String(cfg.gruss || '').trim();
  const g = cfg.grafik && cfg.grafik.art && cfg.grafik.art !== 'keine' && cfg.grafik.hex ? cfg.grafik : null;
  const qrUrl = parseLabelQrUrl(cfg.qrUrl || '').replace(/[\^~]/g, '');
  const qrText = parseLabelQrText(cfg.qrText || '').trim();
  if (!gruss && !g && !qrUrl && !qrText) return y;
  const inner = ZPL_W - 2 * ZPL_M;
  const platz = inner * 0.94;
  y += cfg.rahmen ? 14 : 6;
  if (gruss) {
    const gt = zplText(gruss);
    let gh = 46;
    while (gh > 28 && zplZeilen(gt, gh, platz) > 1) gh -= 2;
    const gz = Math.min(2, zplZeilen(gt, gh, platz));
    body.push(`^FO${ZPL_M},${y}^A0N,${gh},${gh}^FB${inner},${gz},0,C^FD${gt}^FS`);
    y += gz * (gh + 6) + 8;
  }
  if (g && g.bpr > 0 && g.h > 0) {
    const gw = g.bpr * 8;
    const gx = ZPL_M + Math.max(0, Math.round((inner - gw) / 2));
    const tot = g.bpr * g.h;
    body.push(`^FO${gx},${y}^GFA,${tot},${tot},${g.bpr},${g.hex}^FS`);
    y += g.h + 12;
  }
  if (qrUrl || qrText) {
    if (gruss || g || !cfg.rahmen) {
      y += 4;
      body.push(`^FO${ZPL_M},${y}^GB${inner},3,3^FS`);
      y += 14;
    } else {
      y += 6;
    }
    const size = parseLabelSize(cfg.size || getLabelSize()) || parseLabelSize('100x150');
    const zielH = size.h ? Math.round((size.h * ZPL_W) / Math.max(240, size.w)) : 1218;
    const mCode = CODE_MASSE[cfg.code?.groesse] || CODE_MASSE.mittel;
    const codePlatz = cfg.code?.art === 'barcode'
      ? mCode.h + 32
      : cfg.code?.art === 'qr'
        ? QR_MODULE * mCode.mag + 32
        : cfg.code?.art === 'beide'
          ? mCode.h + QR_MODULE * mCode.mag + 48
          : 0;
    const restH = zielH - y - codePlatz - 28;
    const freiH = restH >= 180 ? restH : Math.max(120, Math.min(180, zielH - 360));
    const minMag = restH >= 180 ? 3 : 2;

    if (qrUrl && qrText) {
      const qrMod = qrModuleFuerDaten(qrUrl);
      const maxQrPx = Math.min(256, Math.max(116, freiH - 12));
      let mag = 8;
      while (mag > minMag && qrMod * mag > maxQrPx) mag -= 1;
      const qrPx = qrMod * mag;
      const xText = ZPL_M + qrPx + 22;
      const wText = Math.max(200, inner - qrPx - 22);
      const maxTextH = Math.max(qrPx, freiH - 8);
      let th = 42;
      let zeilen = umbruchAbsatzZeilen(qrText, th, wText * 0.94);
      while (th > 22 && zeilen.length * (th + 5) > maxTextH) {
        th -= 2;
        zeilen = umbruchAbsatzZeilen(qrText, th, wText * 0.94);
      }
      const maxZeilen = Math.max(3, Math.floor(maxTextH / (th + 5)));
      zeilen = zeilen.slice(0, maxZeilen);
      const txtBlockH = zeilen.length * (th + 5);
      const blockH = Math.max(qrPx, txtBlockH);
      const yQr = y + Math.max(0, Math.round((blockH - qrPx) / 2));
      body.push(`^FO${ZPL_M},${yQr}^BQN,2,${mag}^FDQA,${qrUrl}^FS`);
      let yTxt = y + Math.max(0, Math.round((blockH - txtBlockH) / 2));
      for (const z of zeilen) {
        body.push(`^FO${xText},${yTxt}^A0N,${th},${th}^FB${wText},1,0,L^FD${z}^FS`);
        yTxt += th + 5;
      }
      y += blockH + 12;
    } else if (qrUrl) {
      const qrMod = qrModuleFuerDaten(qrUrl);
      const maxQrPx = Math.min(280, Math.max(116, freiH - 12));
      let mag = 8;
      while (mag > minMag && qrMod * mag > maxQrPx) mag -= 1;
      const qrPx = qrMod * mag;
      const xQr = ZPL_M + Math.max(0, Math.round((inner - qrPx) / 2));
      body.push(`^FO${xQr},${y}^BQN,2,${mag}^FDQA,${qrUrl}^FS`);
      y += qrPx + 12;
    } else {
      let th = 44;
      let zeilen = umbruchAbsatzZeilen(qrText, th, platz);
      while (th > 22 && zeilen.length * (th + 6) > freiH) {
        th -= 2;
        zeilen = umbruchAbsatzZeilen(qrText, th, platz);
      }
      const maxZeilen = Math.max(3, Math.floor(freiH / (th + 6)));
      zeilen = zeilen.slice(0, maxZeilen);
      for (const z of zeilen) {
        body.push(`^FO${ZPL_M},${y}^A0N,${th},${th}^FB${inner},1,0,C^FD${z}^FS`);
        y += th + 6;
      }
      y += 8;
    }
  }
  return y;
}

/**
 * 🎨 Flexibles Etikett: große Kopfzeile + wählbare Zeilen (Fach, Name, Straße, Paketdienst, Datum).
 * cfg: { kopf, zeilen: [...], size, rahmen, gruss, grafik }
 */
function buildZplFlexLabel(paket, opts = {}, cfg = {}) {
  const zeilen = Array.isArray(cfg.zeilen) ? cfg.zeilen : [];
  const hat = (z) => zeilen.includes(z);
  const { text: kopf, max: kopfMax } = flexKopf(paket, cfg.kopf, cfg.vintedNr);
  const regal = (paket?.regalplatz || '').toString().trim();
  const carrier = guessCarrier(paket?.trackingNumber, paket?.paketdienst);
  const zeit = formatDatumZeit(paket?.datum || paket?.updatedAt);
  const name = cleanName(paket?.name);
  const street = (paket?.streetAndNumber || '').toString().trim();
  const { index, total } = opts;
  const w = labelWords();
  const inner = ZPL_W - 2 * ZPL_M;
  const nummerOben = !!cfg.vintedNr && !!vintedNummer(paket); // Kopfzeile ist die Vinted-Nummer → Fach und Name darunter zeigen
  const mitFach = hat('fach') && (cfg.kopf !== 'fach' || nummerOben) && !!regal && !(cfg.kopf === 'hausnummer' && !nummerOben && regal.toUpperCase() === kopf.toUpperCase());
  // … außer der Name ist nur der Platzhalter „Vinted 789-160" (stünde dann doppelt da)
  const mitName = hat('name') && (cfg.kopf !== 'name' || nummerOben) && !!name && !(nummerOben && platzhalterCode(name));
  const mitStrasse = hat('strasse') && !!street;
  const initialen = getInitials(paket?.name);
  const mitInitialen = hat('initialen') && cfg.kopf !== 'initialen' && !!initialen;
  const absender = cleanName(String(paket?.absender || '').split(' · ')[0]);
  const mitAbsender = hat('absender') && !!absender;
  // 📦 Vinted: Kurznummer „789-160" — steht auch auf dem Vinted-Aufkleber und in der Vinted-App,
  // daran wird das Paket bei der Abholung erkannt. Gedruckt zusammen mit dem Paketdienst.
  const vintedNr = hat('dienst') ? vintedNummer(paket) : '';

  // Schriftgrößen aus den ECHTEN Zeichenbreiten (FONT0_BREITE): größte Schrift, die in die Breite
  // passt. 6 % Reserve, weil ZPL-Emulationen (Honeywell ZSim, TSC) minimal abweichen können —
  // läuft ein Text über, bricht der Drucker um und druckt die Zeilen ÜBEREINANDER.
  const platz = inner * 0.94;
  const kopfText = zplText(kopf);
  const zeilenBei = (h) => zplZeilen(kopfText, h, platz);
  // Einzeilig bevorzugt (Folkert: kompakt), erst darunter zweizeilig
  let kopfH = kopfMax;
  while (kopfH > 80 && zeilenBei(kopfH) > 1) kopfH -= 1;
  if (zeilenBei(kopfH) > 1) { kopfH = 120; while (kopfH > 40 && zeilenBei(kopfH) > 2) kopfH -= 1; }
  const kopfZeilen = Math.min(3, zeilenBei(kopfH));

  // Fach: normalerweise ein Buchstabe — lange Fachnamen verkleinern statt überdrucken
  const vakText = zplText(w.vak + ' ' + regal);
  let vakH = 130;
  while (vakH > 50 && zplTextBreite(vakText, vakH) > platz) vakH -= 5;

  // Name: 1 oder 2 Zeilen je nach echter Breite (keine reservierte Leerzeile mehr)
  const nameText = zplText(name);
  let nameH = 48;
  while (nameH > 30 && zplZeilen(nameText, nameH, platz) > 2) nameH -= 2;
  const nameZeilen = Math.min(2, zplZeilen(nameText, nameH, platz));

  // Alles dicht untereinander, Fußzeile direkt darunter (nicht mehr am Etikett-Ende)
  const body = [];
  // Oben 90 Punkte (11 mm) frei: Folkerts Drucker schnitt die Kopfzeile bei 50 Punkten oben ab
  let y = 90;
  body.push(`^FO${ZPL_M},${y}^A0N,${kopfH},${kopfH}^FB${inner},${kopfZeilen},0,C^FD${kopfText}^FS`);
  y += Math.round(kopfZeilen * kopfH * 0.95) + 12;
  body.push(`^FO${ZPL_M},${y}^GB${inner},3,3^FS`); // Trennlinie unter dem Namen (Folkerts Entwurf)
  y += 14;
  // Zeilen in der gewählten Reihenfolge. Die dicke Trennlinie steht direkt unter dem Fach —
  // ohne Fach gleich zu Beginn (so sieht die Standard-Reihenfolge aus wie bisher).
  const kleineZeile = (text) => {
    const t = zplText(text);
    let h = 48;
    while (h > 30 && zplTextBreite(t, h) > platz) h -= 2;
    body.push(`^FO${ZPL_M},${y}^A0N,${h},${h}^FB${inner},1,0,L^FD${t}^FS`);
    y += h + 12;
  };
  const linie = () => { body.push(`^FO${ZPL_M},${y}^GB${inner},4,4^FS`); y += 16; };
  if (!mitFach) linie();
  for (const z of zeilen) {
    if (z === 'fach' && mitFach) {
      body.push(`^FO${ZPL_M},${y}^A0N,${vakH},${vakH}^FB${inner},1,0,C^FD${vakText}^FS`);
      y += vakH + 10;
      linie();
    } else if (z === 'name' && mitName) {
      body.push(`^FO${ZPL_M},${y}^A0N,${nameH},${nameH}^FB${inner},${nameZeilen},0,L^FD${nameText}^FS`);
      y += nameZeilen * (nameH + 6) + 6;
    } else if (z === 'initialen' && mitInitialen) {
      // nur die Anfangsbuchstaben des Namens (z. B. aus Datenschutzgründen), gut lesbar groß
      body.push(`^FO${ZPL_M},${y}^A0N,80,80^FB${inner},1,0,L^FD${zplText(initialen)}^FS`);
      y += 92;
    } else if (z === 'strasse' && mitStrasse) {
      // Straße nur auf Wunsch: am Abholpunkt steht dort immer die eigene Shop-Adresse
      kleineZeile(street);
    } else if (z === 'absender' && mitAbsender) {
      kleineZeile(`${w.von}: ${absender}`);
    }
  }
  if (vintedNr && !kopfText.includes(vintedNr)) { // steht die Nummer schon groß oben (Paket ohne Namen), nicht doppelt
    body.push(`^FO${ZPL_M},${y}^A0N,80,80^FB${inner},1,0,L^FD${zplText(vintedNr)}^FS`);
    y += 84;
  }
  const flexCopyLine = formatCopyLine(w.paket, index, total);
  if (flexCopyLine) {
    body.push(`^FO${ZPL_M},${y}^A0N,55,55^FD${zplText(flexCopyLine)}^FS`);
    y += 62;
  }
  // Fußzeile: Paketdienst links, Datum/Uhrzeit rechts
  if (hat('dienst') || hat('datum')) {
    body.push(`^FO${ZPL_M},${y}^GB${inner},3,3^FS`);
    y += 12;
    if (hat('dienst') && carrier) body.push(`^FO${ZPL_M},${y}^A0N,45,45^FD${zplText(carrier)}^FS`);
    if (hat('datum') && zeit) body.push(`^FO${ZPL_M},${y}^A0N,45,45^FB${inner},1,0,R^FD${zplText(zeit)}^FS`);
    y += 45 + ZPL_M;
  } else {
    y += ZPL_M - 16;
  }
  const yKasten = y;
  const yNachGruss = zplUnterKastenBlock(body, y, cfg);
  const hatUnterKasten = yNachGruss !== yKasten;
  y = zplCodeBlock(body, yNachGruss, paket, cfg.code);

  return zplAbschluss(body, y, cfg.size, cfg.rahmen, cfg.rand, hatUnterKasten ? yKasten : null);
}

/**
 * Etikett abschließen: Kopf/Fuß + Etikettengröße.
 *  - feste Etiketten: ^LL = Etikettenhöhe; passt der Inhalt nicht, wird er proportional verkleinert
 *  - Endlos-Rolle: ^LL = Inhaltshöhe, ^MNN = keine Lückensuche → Drucker gibt nur so viel Papier aus wie nötig
 */
export function zplAbschluss(body, yEndeRoh, sizeKey = getLabelSize(), rahmen = isLabelFrameOn(), rand = null, yRahmenRoh = null) {
  const size = parseLabelSize(sizeKey) || parseLabelSize('100x150');
  // 📐 Ränder dieses Geräts in Punkten (203 dpi → 8,12 Punkte je Millimeter). Alle 0 = wie bisher.
  const r = rand || RAND_NULL;
  const dSeite = Math.round((r.seite || 0) * 8.12);
  const dUnten = Math.round((r.unten || 0) * 8.12);
  let dOben = Math.round((r.oben || 0) * 8.12);
  // Die Layouts sind für 100 mm Breite (812 Punkte) gebaut. Andere Breite → alles proportional
  // verkleinern/vergrößern (Positionen, Schrift, Linien). Bei 100 mm bleibt die Ausgabe unverändert.
  // Seitliche Ränder verkleinern die nutzbare Breite.
  const f = (size.w - 2 * dSeite) / ZPL_W;
  let lines = f === 1 ? body : skaliereZplGanz(body, f);
  const yEnde = f === 1 ? yEndeRoh : yEndeRoh * f;
  let yRahmen = yRahmenRoh !== null ? (f === 1 ? yRahmenRoh : yRahmenRoh * f) : yEnde;
  // Nach oben ziehen geht höchstens bis zum obersten Element — sonst rutschte es aus dem Etikett
  if (dOben < 0) {
    let oberstes = rahmen ? Math.round(60 * f) : Infinity;
    for (const l of lines) for (const m of l.matchAll(/\^FO\d+,(\d+)/g)) oberstes = Math.min(oberstes, +m[1]);
    dOben = -Math.min(-dOben, Number.isFinite(oberstes) ? oberstes : 0);
  }
  let LL;
  const kopf = ['^XA', '^CI0', `^PW${size.w}`];
  if (size.h === null) {
    LL = Math.max(200, Math.min(ZPL_H, Math.round(yEnde) + dOben + dUnten));
    kopf.push(`^LL${LL}`, '^MNN');
  } else {
    LL = size.h;
    // kleinste Schrift: 20 Punkte bei 100 mm (wie bisher); auf schmaleren Etiketten darf sie kleiner werden
    const platz = Math.max(60, size.h - dOben - dUnten); // was zwischen oberem und unterem Rand bleibt
    if (yEnde > platz) {
      const sf = (platz - ZPL_M / 2) / yEnde;
      lines = skaliereZpl(lines, sf, f === 1 ? 20 : 10);
      // Nur ein ausdrücklich gesetztes Rahmenende (Grußtext/Bild) mitskalieren — ohne Grußtext blieb der Rahmen bisher
      // am unteren Rand (LL - dUnten); so bleiben bestehende Etiketten byteidentisch (unit_label2, Review 19.10.2026)
      if (yRahmenRoh !== null) yRahmen *= sf;
    }
    kopf.push(`^LL${LL}`);
  }
  // Rahmen: 16 Punkte stark, umschließt den INHALT statt des ganzen Etiketts (Folkert 30.09.:
  // unterer Rand soll direkt unter der Fußzeile liegen). Oben erst bei 60 Punkten (7,5 mm):
  // sein Drucker bedruckt die ersten Millimeter des Etiketts nicht — Rahmen UND Kopfzeile
  // waren oben abgeschnitten. Steht unter dem Kasten ein Grußtext oder Bild (yRahmenRoh), schließt der Rahmen vor dem Grußtext.
  const frameTop = Math.max(0, Math.round(60 * f) + dOben);
  const frameBottom = Math.min(LL - dUnten, Math.round(yRahmen) + dOben) - 6;
  const frame = rahmen && frameBottom - frameTop > 40
    ? [`^FO${6 + dSeite},${frameTop}^GB${size.w - 12 - 2 * dSeite},${frameBottom - frameTop},${Math.max(4, Math.round(16 * f))}^FS`] : [];
  if (dSeite || dOben) lines = verschiebeZpl(lines, dSeite, dOben);
  return [...kopf, '^LH0,0', ...frame, ...lines, '^PQ1', '^XZ'];
}

/** Alles um (dx, dy) Punkte verschieben — für die Ränder dieses Geräts */
export function verschiebeZpl(lines, dx, dy) {
  return lines.map((l) => l.replace(/\^FO(\d+),(\d+)/g, (m, x, y) => `^FO${Math.max(0, +x + dx)},${Math.max(0, +y + dy)}`));
}

/** Alles proportional skalieren (waagrecht UND senkrecht) — für Etiketten, die nicht 100 mm breit sind */
export function skaliereZplGanz(lines, f) {
  const s = (v, min) => Math.max(min, Math.round(v * f));
  return lines.map((l) => l
    .replace(/\^FO(\d+),(\d+)/g, (m, x, y) => `^FO${s(+x, 0)},${s(+y, 0)}`)
    .replace(/\^A0N,(\d+),(\d+)/g, (m, h, w) => `^A0N,${s(+h, 10)},${s(+w, 10)}`)
    .replace(/\^GB(\d+),(\d+),(\d+)/g, (m, w, h, t) => `^GB${s(+w, 1)},${s(+h, 1)},${s(+t, 1)}`)
    .replace(/\^FB(\d+),/g, (m, w) => `^FB${s(+w, 10)},`)
    .replace(/\^BY(\d+),2(?:\.0)?,(\d+)\^BCN,(\d+)/g, (m, mod, h1, h2) => `^BY${Math.max(1, Math.round(+mod * f))},2,${s(+h1, 24)}^BCN,${s(+h2, 24)}`)
    .replace(/\^BQN,2,(\d+)/g, (m, mag) => `^BQN,2,${Math.max(1, Math.floor(+mag * f))}`)
    .replace(/\^GFA,(\d+),\d+,(\d+),([0-9A-F]+)/g, (m, tot, bprStr, hex) => {
      const bprAlt = +bprStr, wAlt = bprAlt * 8, hAlt = Math.floor(hex.length / (bprAlt * 2));
      const bprNeu = Math.max(4, Math.round(bprAlt * f)), wNeu = bprNeu * 8, hNeu = Math.max(8, Math.round(hAlt * f));
      if ((bprNeu === bprAlt && hNeu === hAlt) || hAlt < 2) return m;
      let neuHex = '';
      for (let ry = 0; ry < hNeu; ry++) {
        const sy = Math.min(hAlt - 1, Math.floor((ry * hAlt) / hNeu));
        for (let b = 0; b < bprNeu; b++) {
          let byte = 0;
          for (let bit = 0; bit < 8; bit++) {
            const sx = Math.min(wAlt - 1, Math.floor(((b * 8 + bit) * wAlt) / wNeu));
            const srcByte = parseInt(hex.substr((sy * bprAlt + (sx >> 3)) * 2, 2), 16) || 0;
            if (srcByte & (0x80 >> (sx & 7))) byte |= 0x80 >> bit;
          }
          neuHex += byte.toString(16).toUpperCase().padStart(2, '0');
        }
      }
      const neuTot = bprNeu * hNeu;
      return `^GFA,${neuTot},${neuTot},${bprNeu},${neuHex}`;
    }));
}

/** Senkrechte Maße proportional verkleinern (Position, Schrifthöhe/-breite, Linienstärke, 1-Bit-Grafikhöhe) */
export function skaliereZpl(lines, f, minSchrift = 20) {
  const s = (v, min) => Math.max(min, Math.round(v * f));
  return lines.map((l) => l
    .replace(/\^FO(\d+),(\d+)/g, (m, x, y) => `^FO${x},${s(+y, 0)}`)
    .replace(/\^A0N,(\d+),(\d+)/g, (m, h, w) => `^A0N,${s(+h, minSchrift)},${s(+w, minSchrift)}`)
    .replace(/\^GB(\d+),(\d+),(\d+)/g, (m, w, h, t) => `^GB${w},${s(+h, 1)},${s(+t, 1)}`)
    .replace(/\^BY(\d+),2(?:\.0)?,(\d+)\^BCN,(\d+)/g, (m, mod, h1, h2) => `^BY${mod},2,${s(+h1, 24)}^BCN,${s(+h2, 24)}`)
    .replace(/\^BQN,2,(\d+)/g, (m, mag) => `^BQN,2,${Math.max(1, Math.floor(+mag * f))}`)
    .replace(/\^GFA,(\d+),\d+,(\d+),([0-9A-F]+)/g, (m, tot, bprStr, hex) => {
      const bpr = +bprStr, hAlt = Math.floor(hex.length / (bpr * 2));
      const hNeu = Math.max(8, Math.round(hAlt * f));
      if (hNeu >= hAlt || hAlt < 2) return m;
      let neuHex = '';
      for (let ry = 0; ry < hNeu; ry++) {
        const y0 = Math.floor(ry / f), y1 = Math.min(hAlt - 1, Math.floor((ry + 0.99) / f));
        for (let b = 0; b < bpr; b++) {
          let byte = 0;
          for (let sy = y0; sy <= y1; sy++) byte |= parseInt(hex.substr((sy * bpr + b) * 2, 2), 16);
          neuHex += byte.toString(16).toUpperCase().padStart(2, '0');
        }
      }
      const neuTot = bpr * hNeu;
      return `^GFA,${neuTot},${neuTot},${bpr},${neuHex}`;
    }));
}

function buildZplLabelBytes(paket, opts = {}) {
  return zplBytes(buildZplLines(paket, opts));
}

function istGrenzpaketZentraleLog(str) {
  const s = String(str || '').trim();
  if (!s) return false;
  return (
    /\b\d{1,2}\.\d{2}\.\d{2}\s*\.\s*[a-z]{2,5}\s*:/i.test(s) ||
    /\b(?:RE\s*\d{5,}|Minuskontingent|Ausgleich\s+MK|DG\s+(?:von\s+)?PF\s*\d+|Fehlscan|an\s+Inkasso|Gutschrift\s+da)\b/i.test(s)
  );
}

/**
 * 🇨🇭 Liest Grenzpaket-Metadaten aus paket.notizen (JSON {"gp":1,...}) oder paket.streetAndNumber ("KD 39910 ...").
 */
export function extractGrenzpaketLabelData(paket) {
  if (!paket) return null;
  const rawNotiz = String(paket.notizen || '').trim();
  if (rawNotiz.startsWith('{')) {
    try {
      const obj = JSON.parse(rawNotiz);
      if (obj && obj.gp === 1) {
        const paketTyp = String(obj.paketTyp || 'Paket').trim();
        const isBrief = paketTyp === 'Brief';
        const tarif = isBrief ? 'Bar' : (obj.tarif === 'Bar' ? 'Bar' : obj.tarif === 'Kredit' ? 'Kredit' : 'Unbekannt');
        const rawInfo = String(obj.info || '').trim();
        return {
          kundenNr: String(obj.kundenNr || '').trim(),
          tarif,
          postboxTyp: isBrief ? 'Brief' : String(obj.postboxTyp || '').trim(),
          guthaben: obj.guthaben != null ? obj.guthaben : null,
          paketTyp,
          logId: String(obj.logId || '').trim(),
          paketCode: String(obj.paketCode || '').trim(),
          info: istGrenzpaketZentraleLog(rawInfo) ? '' : rawInfo,
        };
      }
    } catch { /* ignore */ }
  }
  const str = String(paket.streetAndNumber || '');
  const kdMatch = str.match(/\bKD\s*(\d{3,8})\b/i);
  if (kdMatch) {
    const isBrief = /\bbrief\b/i.test(str);
    const isKlein = /\b(?:paketklein|p[äa]ckchen)\b/i.test(str);
    const isBar = isBrief || /bar/i.test(str);
    const isKredit = /kredit|paketfach/i.test(str) && !isBar;
    return {
      kundenNr: kdMatch[1],
      tarif: isBar ? 'Bar' : isKredit ? 'Kredit' : 'Unbekannt',
      postboxTyp: isBrief ? 'Brief' : isBar ? 'Paketfach-Barzahlung' : isKredit ? 'Paketfach-Kredit' : '',
      guthaben: null,
      paketTyp: isBrief ? 'Brief' : isKlein ? 'PaketKlein' : 'Paket',
      logId: '',
      paketCode: '',
      info: '',
    };
  }
  return null;
}

function formatGrenzpaketDatumKw(iso) {
  const d = iso ? new Date(iso) : new Date();
  const valid = !isNaN(d.getTime()) ? d : new Date();
  const p = (n) => String(n).padStart(2, '0');
  const datumStr = `${p(valid.getDate())}.${p(valid.getMonth() + 1)}.${valid.getFullYear()}`;
  const tmp = new Date(Date.UTC(valid.getFullYear(), valid.getMonth(), valid.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const kw = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  return `${datumStr} KW${p(kw)}`;
}

/**
 * 🇨🇭 Original Grenzpaket-Quittungs-Etikett (`delivery_quittance.php`-Layout):
 *   1. Kundennummer-Barcode (oben)
 *   2. Große Kundennummer (z. B. 39910)
 *   3. Kundenname (z. B. Nebojsa Stojanovic)
 *   4. Datum + Kalenderwoche (`27.10.2026 KW44`) + ggf. Vorsortierung/Regalplatz
 *   5. Tarif + Gebühr/Guthaben | Pakettyp (z. B. `Paketfach-Barzahlung 6,00 CHF / 6,00 EUR | Paket` oder `Brief 1 CHF / 1 EUR bar | Brief`)
 *   6. Sendungsnummer (`Keine ID` falls leer) + ggf. Shop-Info
 *   7. Paket-Barcode unten (`B04113610` / `K03930417`) + Klartext für den Zebra-Lagerscanner
 */
export function buildZplGrenzpaketLabel(paket, opts = {}, cfg = {}) {
  const gp = extractGrenzpaketLabelData(paket) || {};
  const kundenNr = String(gp.kundenNr || '').replace(/[^A-Z0-9-]/gi, '').slice(0, 16);
  const nameWithoutKd = String(paket?.name || '').replace(/\(\s*\d{3,8}\s*\)/g, ' ');
  const name = cleanName(nameWithoutKd) || cleanName(paket?.name) || '-';
  const regal = String(paket?.regalplatz || '').trim();
  const tracking = String(paket?.trackingNumber || '').trim();
  const { index, total } = opts;
  const w = labelWords();

  const sizeKey = cfg.size || getLabelSize();
  const size = parseLabelSize(sizeKey) || parseLabelSize('100x75');
  const istQuerRolle76 = size.h !== null && size.h <= 650; // ZEGA 102×76 mm im Zebra ZD421 (812 breit × 609 Vorschub)

  // Gemeinsame Textzeilen aufbereiten
  const topBcData = kundenNr || '';
  const kopfDisplay = zplText((kundenNr || extractHausnummer(paket?.streetAndNumber) || getInitials(name) || '-').toUpperCase());
  const datumKw = formatGrenzpaketDatumKw(paket?.datum || paket?.updatedAt);
  const datumZeileRaw = regal ? `${datumKw}  [${regal}]` : datumKw;
  const aktTyp = gp.paketTyp || 'Paket';
  const bonTypName = aktTyp === 'Brief' ? 'Brief' : aktTyp === 'PaketKlein' ? 'Päckchen' : 'Paket';
  const tarifLabel = aktTyp === 'Brief'
    ? 'Brief 1 CHF / 1 EUR bar'
    : gp.tarif === 'Bar'
      ? `Paketfach-Barzahlung ${aktTyp === 'PaketKlein' ? '3,00 CHF / 3,00 EUR' : '6,00 CHF / 6,00 EUR'}`
      : [gp.postboxTyp || 'Paketfach-Kredit', gp.guthaben != null && gp.guthaben !== '' ? String(gp.guthaben) : ''].filter(Boolean).join(' ');
  const copySuffix = formatCopyLine(w.paket, index, total);
  const tarifZeileRaw = [`${tarifLabel}|${bonTypName}`, copySuffix].filter(Boolean).join(' · ');
  const infoTeile = [];
  if (gp.info && gp.info !== tracking) infoTeile.push(gp.info);
  infoTeile.push(tracking || 'Keine ID');
  const infoTextRaw = infoTeile.join(' · ').slice(0, 50);
  const rawPkgCode = gp.paketCode
    || codeDaten(paket)
    || (paket?.serverId ? `GP-${paket.serverId}` : (paket?.id ? `GP-${String(paket.id).replace(/[^A-Za-z0-9]/g, '').slice(-12)}` : ''))
    || (kundenNr ? `KD${kundenNr}` : '');
  const pkgCode = String(rawPkgCode || '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 36);

  // 🖨️ Zebra ZD421 mit ZEGA 102×76 mm Rolle (Art.-Nr. B101020760001):
  // Die Rolle liegt 102 mm breit (812 Punkte) × 76 mm Vorschub (609 Punkte) im Drucker.
  // Gedruckt wird um 90° gedreht (^A0R / ^BCR), sodass das abgerissene Etikett hochkant (76 mm breit × 102 mm hoch)
  // exakt wie das Original-Grenzpaket-Etikett vorne auf die schmale Paketseite geklebt werden kann.
  if (istQuerRolle76) {
    const W = size.w || 812; // 102 mm = Hochkant-Höhe des Etiketts (von rechts nach links)
    const H = size.h || 609; // 76 mm  = Hochkant-Breite des Etiketts (von oben nach unten)
    const r = cfg.rand || RAND_NULL;
    const dSeite = Math.round((r.seite || 0) * 8.12);
    const dOben = Math.round((r.oben || 0) * 8.12);
    const usableW = (H - 36) * 0.94;
    const lines = [];
    let xCursor = W - 26 - dOben;

    const pushRotatedLine = (rawStr, maxH, minH = 20, gapAfter = 12) => {
      const t = zplText(rawStr);
      if (!t) return;
      let h = maxH;
      while (h > minH && zplTextBreite(t, h) > usableW) h -= 2;
      const tw = zplTextBreite(t, h);
      const y = Math.max(14, Math.round((H - tw) / 2) + dSeite);
      xCursor -= h;
      const x = Math.max(10, xCursor);
      lines.push(`^FO${x},${y}^A0R,${h},${h}^FD${t}^FS`);
      xCursor -= gapAfter;
    };

    // 1. Kundennummer-Barcode oben (^BCR)
    if (topBcData.length >= 3) {
      const bh = 62;
      const modCount = code128Module(topBcData);
      const modul = modCount * 3 <= usableW ? 3 : 2;
      const bcW = modCount * modul;
      const y = Math.max(14, Math.round((H - bcW) / 2) + dSeite);
      xCursor -= bh;
      lines.push(`^FO${Math.max(10, xCursor)},${y}^BY${modul},2,${bh}^BCR,${bh},N,N,N^FD${topBcData}^FS`);
      xCursor -= 18;
    }

    // 2. Große Kundennummer (z. B. 27486)
    pushRotatedLine(kopfDisplay, 156, 76, 16);

    // 3. Kundenname zweizeilig (Vorname / Nachname wie auf dem Zebra ZD421 Originaletikett)
    const words = String(name || '-').trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      const vornameZeile = words.slice(0, -1).join(' ');
      const nachnameZeile = words[words.length - 1];
      pushRotatedLine(vornameZeile, 58, 30, 8);
      pushRotatedLine(nachnameZeile, 58, 30, 16);
    } else {
      pushRotatedLine(words[0] || '-', 58, 30, 16);
    }

    // 4. Datum + KW (+ ggf. Vorsortierung/Lagerort)
    pushRotatedLine(datumZeileRaw, 38, 24, 14);

    // 5. Tarif + Guthaben/Gebühr | Pakettyp
    pushRotatedLine(tarifZeileRaw, 32, 20, 14);

    // 6. Sendungsnummer / Info (bzw. "Keine ID")
    pushRotatedLine(infoTextRaw, 30, 20, 18);

    // 7. Paket-Barcode unten (^BCR) + Klartext darunter (z. B. K03931266 / B04019255)
    if (pkgCode.length >= 3) {
      const bh = 90;
      const modCount = code128Module(pkgCode);
      let modul = 3;
      while (modul > 1 && modCount * modul > usableW) modul -= 1;
      const bcW = modCount * modul;
      const y = Math.max(14, Math.round((H - bcW) / 2) + dSeite);
      xCursor -= bh;
      lines.push(`^FO${Math.max(10, xCursor)},${y}^BY${modul},2,${bh}^BCR,${bh},N,N,N^FD${pkgCode}^FS`);
      xCursor -= 8;
      pushRotatedLine(pkgCode, 34, 22, 8);
    }

    const frame = cfg.rahmen
      ? [`^FO12,12^GB${W - 24},${H - 24},8^FS`]
      : [];
    return ['^XA', '^CI0', `^PW${W}`, `^LL${H}`, '^LH0,0', ...frame, ...lines, '^PQ1', '^XZ'];
  }

  // Fallback für Hochformat-Rollen (100×150 mm oder Endlos):
  const inner = ZPL_W - 2 * ZPL_M;
  const platz = inner * 0.94;
  const body = [];
  let y = cfg.rahmen ? 62 : 24;

  // 1. Kundennummer-Barcode oben
  if (topBcData.length >= 3) {
    const modCount = code128Module(topBcData);
    const modul = modCount * 3 <= inner ? 3 : 2;
    const bcW = modCount * modul;
    const bcX = ZPL_M + Math.max(0, Math.round((inner - bcW) / 2));
    body.push(`^FO${bcX},${y}^BY${modul},2,54^BCN,54,N,N,N^FD${topBcData}^FS`);
    y += 62;
  }

  // 2. Große Kundennummer
  let kopfH = 148;
  while (kopfH > 72 && zplTextBreite(kopfDisplay, kopfH) > platz) kopfH -= 4;
  body.push(`^FO${ZPL_M},${y}^A0N,${kopfH},${kopfH}^FB${inner},1,0,C^FD${kopfDisplay}^FS`);
  y += Math.round(kopfH * 0.92) + 8;
  body.push(`^FO${ZPL_M},${y}^GB${inner},3,3^FS`);
  y += 12;

  // 3. Kundenname
  const nameClean = zplText(name);
  let nameH = 54;
  while (nameH > 32 && zplZeilen(nameClean, nameH, platz) > 2) nameH -= 2;
  const nameZeilen = Math.min(2, zplZeilen(nameClean, nameH, platz));
  body.push(`^FO${ZPL_M},${y}^A0N,${nameH},${nameH}^FB${inner},${nameZeilen},0,C^FD${nameClean}^FS`);
  y += nameZeilen * (nameH + 4) + 6;

  // 4. Datum + KW + evtl. Vorsortierung/Lagerort
  const datumZeile = zplText(datumZeileRaw);
  let datumH = 38;
  while (datumH > 26 && zplTextBreite(datumZeile, datumH) > platz) datumH -= 2;
  body.push(`^FO${ZPL_M},${y}^A0N,${datumH},${datumH}^FB${inner},1,0,C^FD${datumZeile}^FS`);
  y += datumH + 8;

  // 5. Tarif + Gebühr/Guthaben + Pakettyp
  const tarifZeile = zplText(tarifZeileRaw);
  let tarifH = 34;
  while (tarifH > 24 && zplTextBreite(tarifZeile, tarifH) > platz) tarifH -= 2;
  body.push(`^FO${ZPL_M},${y}^A0N,${tarifH},${tarifH}^FB${inner},1,0,C^FD${tarifZeile}^FS`);
  y += tarifH + 8;

  // 6. Info / Sendungsnummer
  const infoText = zplText(infoTextRaw);
  if (infoText) {
    let infoH = 32;
    while (infoH > 22 && zplTextBreite(infoText, infoH) > platz) infoH -= 2;
    body.push(`^FO${ZPL_M},${y}^A0N,${infoH},${infoH}^FB${inner},1,0,C^FD${infoText}^FS`);
    y += infoH + 8;
  }

  // 7. Paket-Barcode unten + Klartext darunter
  if (pkgCode.length >= 3) {
    y += 4;
    const modCount = code128Module(pkgCode);
    let modul = 3;
    while (modul > 1 && modCount * modul > inner) modul -= 1;
    const bcW = modCount * modul;
    const bcX = ZPL_M + Math.max(0, Math.round((inner - bcW) / 2));
    body.push(`^FO${bcX},${y}^BY${modul},2,74^BCN,74,N,N,N^FD${pkgCode}^FS`);
    y += 80;
    body.push(`^FO${ZPL_M},${y}^A0N,34,34^FB${inner},1,0,C^FD${zplText(pkgCode)}^FS`);
    y += 40;
  }

  const yKasten = y + 8;
  const yNachGruss = zplUnterKastenBlock(body, yKasten, cfg);
  const hatUnterKasten = yNachGruss !== yKasten;
  return zplAbschluss(body, hatUnterKasten ? yNachGruss + 10 : yKasten, cfg.size, cfg.rahmen, cfg.rand, hatUnterKasten ? yKasten : null);
}

/**
 * ZPL-Zeilen eines Etiketts. cfg = Etikett-Einstellungen (Standard: die dieses Geräts) — der
 * Etikett-Dialog übergibt hier noch nicht gespeicherte Einstellungen für die Vorschau.
 */
export function buildZplLines(paket, opts = {}, cfg = getLabelCfg()) {
  // opts.zusatz (Turbo PaketPilot) + Paletten-/Großmengen-Info in der Grußzeile unter dem Kasten
  const anz = parseInt(paket?.paketAnzahl, 10) || 1;
  const palText = [
    paket?.palettenTyp ? String(paket.palettenTyp).trim() : '',
    (anz > 5 || (paket?.palettenTyp && anz > 1)) ? `${anz} Pakete` : '',
  ].filter(Boolean).join(' · ');
  const effZusatz = [opts.zusatz, palText].filter(Boolean).join(' · ');
  if (effZusatz) cfg = { ...cfg, gruss: [effZusatz, cfg.gruss].filter(Boolean).join('  ') };
  const isGpPaket = String(paket?.notizen || '').trim().startsWith('{"gp":1');
  if (cfg.layout === 'grenzpaket' || (isGpPaket && cfg.layout !== 'eigen')) {
    return buildZplGrenzpaketLabel(paket, opts, cfg);
  }
  const explicitSort = getExplicitCarrierSort(paket?.paketdienst, paket?.trackingNumber);
  const effVintedNr = explicitSort ? explicitSort === 'kurznummer' : cfg.vintedNr;
  if (explicitSort === 'nachname' && cfg.layout !== 'eigen') {
    return buildZplSurnameLabel(paket, opts, { size: cfg.size, rahmen: cfg.rahmen, rand: cfg.rand, vintedNr: false, code: cfg.code, gruss: cfg.gruss, grafik: cfg.grafik, qrUrl: cfg.qrUrl, qrText: cfg.qrText });
  }
  if (explicitSort === 'hausnummer' && cfg.layout === 'nachname') {
    return buildZplFlexLabel(paket, opts, { kopf: 'hausnummer', zeilen: ['fach', 'name', 'dienst', 'datum'], size: cfg.size, rahmen: cfg.rahmen, rand: cfg.rand, vintedNr: false, code: cfg.code, gruss: cfg.gruss, grafik: cfg.grafik, qrUrl: cfg.qrUrl, qrText: cfg.qrText });
  }
  if (cfg.layout === 'nachname') return buildZplSurnameLabel(paket, opts, { size: cfg.size, rahmen: cfg.rahmen, rand: cfg.rand, vintedNr: effVintedNr, code: cfg.code, gruss: cfg.gruss, grafik: cfg.grafik, qrUrl: cfg.qrUrl, qrText: cfg.qrText });
  if (cfg.layout === 'eigen') {
    const effKopf = explicitSort === 'hausnummer' ? 'hausnummer' : (explicitSort === 'nachname' ? 'nachname' : cfg.kopf);
    return buildZplFlexLabel(paket, opts, { kopf: effKopf, zeilen: cfg.zeilen, size: cfg.size, rahmen: cfg.rahmen, rand: cfg.rand, vintedNr: effVintedNr, code: cfg.code, gruss: cfg.gruss, grafik: cfg.grafik, qrUrl: cfg.qrUrl, qrText: cfg.qrText });
  }
  const hausnummer = isForwardingAddress(paket?.streetAndNumber)
    ? ''
    : extractHausnummer(paket?.streetAndNumber);
  const name = cleanName(paket?.name);
  const street = (paket?.streetAndNumber || '').toString().trim();
  const regal = (paket?.regalplatz || '').toString().trim();
  const datum = formatDatum(paket?.datum || paket?.updatedAt);
  const { index, total } = opts;
  // 📦 „Vinted nach Nummer einsortieren": statt Hausnummer/Initialen steht die Kurznummer groß oben
  const vNr = effVintedNr ? vintedNummer(paket) : '';
  const gross = vNr || (hausnummer || getInitials(name) || '-').toUpperCase();
  const w = labelWords();
  const inner = ZPL_W - 2 * ZPL_M;

  // Große Kennung: Schriftgröße aus Zeichenzahl (A0 ≈ 0,6 × Höhe pro Zeichen), gedeckelt.
  const bigH = Math.max(120, Math.min(420, Math.floor(inner / (Math.max(1, gross.length) * 0.62))));

  const z = [];
  let y = ZPL_M + 10;
  z.push(`^FO${ZPL_M},${y}^A0N,${bigH},${bigH}^FB${inner},1,0,C^FD${zplText(gross)}^FS`);
  y += bigH + 30;
  z.push(`^FO${ZPL_M},${y}^GB${inner},4,4^FS`);
  y += 30;
  // (steht die Vinted-Nummer groß oben, wird der Platzhaltername „Vinted 789-160" nicht noch einmal gedruckt)
  if (name && !(vNr && platzhalterCode(name))) { z.push(`^FO${ZPL_M},${y}^A0N,70,70^FB${inner},2,0,L^FD${zplText(name)}^FS`); y += 170; }
  if (street) { z.push(`^FO${ZPL_M},${y}^A0N,55,55^FB${inner},1,0,L^FD${zplText(street)}^FS`); y += 80; }
  if (regal) { z.push(`^FO${ZPL_M},${y}^A0N,60,60^FD${zplText(w.regal + ': ' + regal)}^FS`); y += 80; }
  if (cfg.dienst) { // 🚚 nur wenn eingeschaltet — sonst Etikett wie bisher
    const dienst = guessCarrier(paket?.trackingNumber, paket?.paketdienst);
    if (dienst) { z.push(`^FO${ZPL_M},${y}^A0N,55,55^FD${zplText(dienst)}^FS`); y += 75; }
  }
  if (datum) { z.push(`^FO${ZPL_M},${y}^A0N,40,40^FD${zplText(w.datum + ': ' + datum)}^FS`); y += 60; }
  const stdCopyLine = formatCopyLine(w.paket, index, total);
  if (stdCopyLine) { z.push(`^FO${ZPL_M},${y}^A0N,60,60^FD${zplText(stdCopyLine)}^FS`); y += 80; }
  const yKasten = y + ZPL_M;
  const yNachGruss = zplUnterKastenBlock(z, yKasten, cfg);
  const hatUnterKasten = yNachGruss !== yKasten;
  y = zplCodeBlock(z, hatUnterKasten ? yNachGruss : y, paket, cfg.code);
  return zplAbschluss(z, hatUnterKasten ? y + 12 : y + ZPL_M, cfg.size, cfg.rahmen, cfg.rand, hatUnterKasten ? yKasten : null);
}

/** Beispielpaket für Testdruck und Vorschau */
export function beispielPaket(layout = getLabelLayout()) {
  if (layout === 'grenzpaket') {
    return {
      name: 'Nebojsa Stojanovic',
      streetAndNumber: 'KD 39910 · Barzahlung',
      trackingNumber: 'CR062810493UA',
      regalplatz: 'P6-C',
      notizen: JSON.stringify({
        gp: 1,
        kundenNr: '39910',
        tarif: 'Bar',
        postboxTyp: 'Paketfach-Barzahlung',
        paketTyp: 'Paket',
        paketCode: 'B04113610',
      }),
      datum: new Date().toISOString(),
    };
  }
  return {
    name: layout === 'nachname' ? 'Maria dos Santos Hernandez' : 'Jan de Vries',
    streetAndNumber: 'Teststraat 12',
    trackingNumber: '3SXXX0000000000',
    regalplatz: 'B7',
    datum: new Date().toISOString(),
  };
}

function zplBytes(z) {
  const txt = z.join('\r\n') + '\r\n';
  const out = new Uint8Array(txt.length);
  for (let i = 0; i < txt.length; i++) out[i] = txt.charCodeAt(i) & 0xff;
  return out;
}

// ============================================================
// Drucken
// ============================================================

function toBase64(uint8) {
  let binary = '';
  for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
  return btoa(binary);
}

/**
 * 🔌 Funktionsprüfung: gekoppelte BT-Geräte + Verbindungszustand dieses Geräts.
 * null = Plugin/Methode nicht verfügbar (alte APK, Browser) — Anzeige degradiert dann.
 */
export async function getBtStatus() {
  if (!isPrinterSupported()) return null;
  try {
    await requestBtPermission();
    return await bt().btStatus();
  } catch {
    return null;
  }
}

/**
 * Direktdruck OHNE Fallback — wird vom Foto-Handy benutzt, wenn es einen per SSE
 * weitergereichten Druckauftrag ausführt (sonst könnte ein Fehler dort wieder
 * zurück-relayt werden → Schleife).
 */
// 🌐 tcp://host:port → Netzwerkdrucker (braucht APK mit BtPrint.printTcp)
export function isNetworkAddress(address) {
  return /^tcp:\/\//i.test(String(address || ''));
}

async function printTcp(address, base64) {
  const m = String(address).match(/^tcp:\/\/([^:/]+)(?::(\d+))?/i);
  if (!m) throw new Error(await uiText('ui.svc.invalidNetworkAddress', 'Ungültige Netzwerkadresse'));
  try {
    return await bt().printTcp({ host: m[1], port: m[2] ? parseInt(m[2], 10) : 9100, data: base64 });
  } catch (e) {
    if (/not implemented|UNIMPLEMENTED/i.test(String(e?.message || e?.code || ''))) {
      throw new Error(await uiText('ui.svc.networkPrintNeedsUpdate', 'Netzwerkdruck braucht das neueste App-Update (Play Store).'));
    }
    throw e;
  }
}

// ⚡ Bluetooth-Feinabstimmung je Druckertyp (23.10.2026, braucht APK ab 2.4.6 — ältere ignorieren die Angaben und
// drucken wie bisher). Bixolon (CPCL, mobil): kleiner Puffer → kleine Blöcke mit Pause; schläft nach Leerlauf ein →
// erster Verbindungsversuch scheitert oft. Tischdrucker (ZPL: TSC/Honeywell/Zebra): großer Puffer → große Blöcke.
// holdMs: so lange hält das Handy den Drucker nach dem Senden noch fest — bisher 2,5 s; bei zwei Handys an einem
// Drucker (Green Goat) musste das zweite so lange warten und scheiterte dabei, plus 2 s bis zum nächsten Versuch.
export function druckProfil(lang = getPrinterLang()) {
  // ⚠️ TSC/Honeywell über Bluetooth-SPP haben kein Hardware-Flow-Control (RTS/CTS) auf dem internen
  // BT-UART-Modul: 1024-B-Blöcke mit 5 ms Pause + 600 ms holdMs (aus 2.4.6) ließen bei großen Etiketten
  // (z. B. 5,2 KB mit Halloween-Grafik ^GFA) den UART-Puffer überlaufen bzw. schnitten ^XZ beim close() ab
  // → App meldet „Erfolg", Drucker druckt nichts. Deshalb wieder 128 B / 20 ms / 1800 ms wie in 2.4.5,
  // kombiniert mit 6 Versuchen à 800 ms für den 2-Handy-Betrieb.
  return lang === 'zpl'
    ? { chunk: 128, pauseMs: 20, holdMs: 1800, retryMs: 800, tries: 6 }
    : { chunk: 128, pauseMs: 20, holdMs: 1000, retryMs: 1000, tries: 4 };
}

// ⏱️ Gemessene Druckzeiten (letzte 30, je Gerät) — Zusammenfassung geht mit der Geräte-Info an den Server (Admin)
const ZEITEN_KEY = 'kiosk_druck_zeiten';
function merkeDruckZeit(t) {
  try {
    const liste = JSON.parse(localStorage.getItem(ZEITEN_KEY) || '[]');
    liste.push({ total: t.totalMs, connect: t.connectMs, send: t.sendMs, versuche: t.attempts, bytes: t.bytes, hold: t.holdMs, weg: t.weg || null, at: Date.now() });
    localStorage.setItem(ZEITEN_KEY, JSON.stringify(liste.slice(-30)));
    if (liste.length % 10 === 0) window.dispatchEvent(new CustomEvent('kiosk:druck-zeiten'));
  } catch { /* */ }
}
/** { n, gesamtMs, verbindenMs, sendenMs (je Median), versucheMax, mehrfach (Drucke mit >1 Versuch), bytes, holdMs, bis } | null */
export function druckZeitenZusammenfassung() {
  try {
    const l = JSON.parse(localStorage.getItem(ZEITEN_KEY) || '[]').filter((x) => x && Number.isFinite(x.total));
    if (!l.length) return null;
    const median = (k) => { const v = l.map((x) => Number(x[k]) || 0).sort((a, b) => a - b); return v[Math.floor(v.length / 2)]; };
    return {
      n: l.length, gesamtMs: median('total'), verbindenMs: median('connect'), sendenMs: median('send'),
      versucheMax: Math.max(...l.map((x) => Number(x.versuche) || 1)), mehrfach: l.filter((x) => (Number(x.versuche) || 1) > 1).length,
      bytes: median('bytes'), holdMs: l[l.length - 1].hold ?? null, bis: new Date(l[l.length - 1].at).toISOString(),
      // Verbindungsweg (APK ab 2.4.6): standard | unsicher | kanal1 — wie oft welcher geklappt hat
      wege: l.reduce((w, x) => (x.weg ? { ...w, [x.weg]: (w[x.weg] || 0) + 1 } : w), {}),
    };
  } catch { return null; }
}

export async function printRawLocal(address, base64) {
  if (!isPrinterSupported()) throw new Error(await uiText('ui.svc.printerAndroidOnly', 'Drucker nur unter Android verfügbar'));
  if (isNetworkAddress(address)) { await printTcp(address, base64); return; }
  await requestBtPermission();
  await bt().print({ address, data: base64, ...druckProfil() });
}

// 🖨️ Druck-Warteschlange (21.10.2026): Aufträge nacheinander statt gleichzeitig. Jeder Auftrag baut seine eigene
// Bluetooth-Verbindung auf; zwei gleichzeitige (schnelles Scannen in „Vinted erfassen"/Turbo) stören sich am Drucker.
// Der Aufrufer wartet nur auf SEINEN Auftrag — das Scannen läuft unabhängig davon weiter.
let druckKette = Promise.resolve();
export function printRaw(address, uint8) {
  const auftrag = druckKette.then(() => printRawSofort(address, uint8));
  druckKette = auftrag.catch(() => {});
  return auftrag;
}

async function printRawSofort(address, uint8) {
  if (!isPrinterSupported()) throw new Error(await uiText('ui.svc.printerAndroidOnly', 'Drucker nur unter Android verfügbar'));
  const data = toBase64(uint8);
  // 🌐 Netzwerkdrucker: direkt per TCP — kein BT, kein Foto-Handy-Relay
  if (isNetworkAddress(address)) return printTcp(address, data);
  await requestBtPermission(); // sicherstellen, dass die BT-Berechtigung erteilt ist
  // 🖨️ Wahl des Druck-Geräts (Kamera-Station-Panel): 'phone' = Aufträge gehen DIREKT
  // ans Foto-Handy (besserer BT-Chip), ohne erst am Tablet zu scheitern.
  const via = (() => { try { return localStorage.getItem('kiosk_print_via') || 'tablet'; } catch { return 'tablet'; } })();
  if (via === 'phone') {
    const { relaySignal } = await import('./stationService');
    await relaySignal('station:print', { address, data });
    return;
  }
  try {
    // ⏱️ Das native Plugin liefert die Phasenzeiten zurück (bond/connect/send).
    // Damit lässt sich beantworten, ob die Wartezeit vom Drucker (connectMs hoch,
    // attempts > 1 = er war eingeschlafen) oder von unserer Software kommt.
    const t0 = Date.now();
    const timing = await bt().print({ address, data, ...druckProfil() });
    const total = Date.now() - t0;
    console.info('🖨️ Druck-Zeiten:', { totalMs: total, ...(timing || {}) });
    merkeDruckZeit({ totalMs: total, ...(timing || {}) });
    return { totalMs: total, ...(timing || {}) };
  } catch (e) {
    // 🖨️➡️📱 Fallback über das Foto-Handy NUR im Bench-/Station-Betrieb (sonst verschluckt
    // relaySignal('station:print') mit 200 OK jeden Bluetooth-Fehler auf normalen Handys,
    // ohne dass eine Kamera-Station zuhört — weder Fehler-Toast noch app_errors-Eintrag!).
    const hatStation = (() => {
      try {
        return localStorage.getItem('kiosk_device_role') === 'bench'
          || Number(localStorage.getItem('kiosk_bench_focusM')) > 0;
      } catch { return false; }
    })();
    if (!hatStation) throw e;
    try {
      const { relaySignal } = await import('./stationService');
      await relaySignal('station:print', { address, data });
      reportError('druck_weitergereicht', e, { drucker: getPrinterConfig()?.name || '', typ: getPrinterLang() });
      console.warn('🖨️ Tablet-Druck fehlgeschlagen → Auftrag ans Foto-Handy weitergereicht:', e?.message || e);
    } catch {
      throw e; // auch das Weiterreichen ging nicht → ursprünglichen Fehler melden
    }
  }
}

/**
 * Druckt EIN Etikett für ein Paket am konfigurierten Drucker.
 * Wirft bei Fehler (für UI-Feedback). opts = {index, total} für Mehrfach-Etiketten.
 */
export async function printOne(paket, opts = {}) {
  const config = getPrinterConfig();
  if (!config?.address) throw new Error(await uiText('ui.svc.noPrinterSelected', 'Kein Drucker ausgewählt'));
  if (!opts.forceLocal && (config.address === 'remote-bridge' || config.remote)) {
    const { sendRemotePrintJob } = await import('./deviceService');
    return await sendRemotePrintJob({ paket, copies: 1 });
  }
  try {
    return await printRaw(config.address, buildLabelBytes(paket, opts));
  } catch (e) {
    reportError('druck_fehler', e, { drucker: config.name || '', adresse: config.address || '', typ: getPrinterLang(), netz: isNetworkAddress(config.address) });
    throw e;
  }
}

/**
 * Druckt ein Beispiel-Etikett im ESC/POS-Format (Hausnummer groß via GS !).
 * Testet, ob der Drucker ESC/POS direkt akzeptiert (Unified-Auto-Erkennung) —
 * ganz ohne Moduswechsel.
 */
export async function testPrint(address, { direkt = false } = {}) {
  // Test: keine Hausnummer -> Initialen; verschmutzter OCR-Name -> bereinigt
  const zpl = getPrinterLang() === 'zpl';
  const layout = getLabelLayout();
  const mitDienst = (zpl && layout === 'eigen') || isLabelCarrierOn();
  // direkt = ohne Umweg übers Foto-Handy: beim Einrichten soll ein Fehler DIESES Geräts sichtbar werden
  const senden = direkt ? (adr, bytes) => printRawLocal(adr, toBase64(bytes)) : printRaw;
  try {
    const testPaket = zpl && layout === 'grenzpaket'
      ? beispielPaket('grenzpaket')
      : {
          ...(mitDienst ? { paketdienst: 'DHL' } : {}),
          name: zpl ? (layout === 'nachname' ? 'Maria dos Santos Hernandez' : 'Jan de Vries') : '´´ für Max Mustermann Bobolution GmbH',
          streetAndNumber: zpl ? 'Teststraat 12' : undefined,
          trackingNumber: zpl ? '3SXXX0000000000' : undefined,
          regalplatz: 'B7',
          datum: new Date().toISOString(),
        };
    await senden(address, buildLabelBytes(testPaket));
  } catch (e) {
    // gerade beim Einrichten wichtig: so sehen wir, bei wem der Drucker nicht will
    reportError('druck_fehler', e, { test: true, drucker: getPrinterConfig()?.name || '', adresse: address || '', typ: getPrinterLang(), netz: isNetworkAddress(address) });
    throw e;
  }
}

/**
 * Stilles Drucken eines einzelnen Etiketts (Auto-Druck bei Einzelpaket).
 * Fehler werden geschluckt — Druck darf den Speichervorgang nie blockieren.
 */
export async function printLabel(paket) {
  try {
    if (!isPrinterSupported() || !isAutoPrintEnabled()) return;
    const config = getPrinterConfig();
    if (!config?.address) return;
    if (config.address === 'remote-bridge' || config.remote) {
      const { sendRemotePrintJob } = await import('./deviceService');
      await sendRemotePrintJob({ paket, copies: 1 });
      return;
    }
    await printRaw(config.address, buildLabelBytes(paket));
  } catch (e) {
    console.warn('🖨️ Etikettendruck fehlgeschlagen (nicht kritisch):', e?.message || e);
    const cfg = getPrinterConfig();
    reportError('druck_fehler', e, { auto: true, drucker: cfg?.name || '', adresse: cfg?.address || '', typ: getPrinterLang() });
  }
}
